
// Copy one object, change key + path + headings + bullets.
// Keep CTA consistent.
// Add its path to App.jsx routes using <LandingPage />.

export const LANDING_PAGES = [
  // Core offers
  {
    key: "online-presence-audit",
    path: "/online-presence-audit",
    title: "Online Presence Audit | Find What’s Costing You Enquiries | 2merative Web Design",
    description:
      "Get a clear online presence audit for your business. We check your website, Google visibility, trust signals, and conversion path, then give you a simple fix plan.",
    heroHeading: "Online Presence Audit That Shows What’s Costing You Enquiries",
    heroSubheading:
      "If customers are not calling, messaging, or booking, something is usually broken in the path. We find it, explain it clearly, and give you a simple fix plan.",
    problemHeading: "You Can Be Online And Still Hard To Find",
    problemParagraphs: [
      "A Facebook page, an old website, or an incomplete Google profile can look fine at a glance. But small issues can quietly stop people from taking action.",
      "Most businesses do not need more content. They need fewer friction points and clearer trust signals.",
      "This audit shows what is happening, in plain language, with practical fixes you can apply."
    ],
    problemBoxHeading: "Common signs you need an audit",
    problemBullets: [
      "You show up lower than competitors in Google",
      "People visit your site but do not contact you",
      "Your website feels slow or confusing on mobile",
      "Your Google Business Profile is incomplete or inconsistent",
      "Customers ask the same basic questions repeatedly",
      "You are not sure what to fix first"
    ],
    offerHeading: "What We Check",
    offerLead: "A practical review across the things that actually affect enquiries. Clear priorities, not a long technical report.",
    offerCards: [
      { title: "Visibility", text: "Search terms, local signals, and the structure that helps you show up." },
      { title: "Trust", text: "Consistency, proof, and clarity that helps people believe you." },
      { title: "Conversion path", text: "How easy it is to call, message, book, or request a quote." },
      { title: "Quick wins", text: "Fast changes that reduce friction without a rebuild." },
      { title: "Priority list", text: "The few fixes that make the biggest difference first." },
      { title: "Optional scope", text: "If you want us to fix it, you get a clear scoped option." }
    ],
    stepsHeading: "How It Works",
    steps: [
      { title: "Quick chat", text: "We confirm goals, audience, and what a good enquiry looks like." },
      { title: "Audit", text: "We review the key points that affect visibility and action." },
      { title: "Fix plan", text: "You get a clear list of what to fix first and why." },
      { title: "Support", text: "You can apply it yourself or ask us to implement it." }
    ],
    finalHeading: "Want A Clear Fix Plan For Your Online Presence?",
    finalLead: "If you are unsure what to fix first, this is the fastest way to get clarity and a simple plan."
  },

  {
    key: "website-speed-fix",
    path: "/website-speed-fix",
    title: "Website Speed Fix | Faster Load, More Enquiries | 2merative Web Design",
    description:
      "Speed up a slow website without a rebuild. Improve mobile experience, reduce bounce, and make your pages feel instant.",
    heroHeading: "Website Speed Fix That Makes Your Site Feel Fast Again",
    heroSubheading:
      "Slow sites lose enquiries. We find what is dragging performance down and fix the biggest speed bottlenecks first.",
    problemHeading: "Speed Is A Trust Signal",
    problemParagraphs: [
      "If a page takes too long, people leave. They do not read the rest.",
      "Most speed problems come from a few common issues like oversized images, messy scripts, and heavy themes.",
      "This service focuses on practical improvements you can feel immediately."
    ],
    problemBoxHeading: "Common speed symptoms",
    problemBullets: [
      "Mobile pages take ages to load",
      "Images look sharp but are massive",
      "Animations cause jank on scroll",
      "Plugins and trackers slow everything down",
      "Core Web Vitals are poor",
      "You are embarrassed to share your link"
    ],
    offerHeading: "What You Get",
    offerLead: "A focused speed pass that targets the biggest wins first.",
    offerCards: [
      { title: "Performance scan", text: "Identify the main bottlenecks and what to fix first." },
      { title: "Image optimisation", text: "Right-size images so pages load quickly." },
      { title: "Script clean-up", text: "Remove or delay heavy scripts that block rendering." },
      { title: "Mobile stability", text: "Fix layout shift and scroll jank where possible." },
      { title: "Before/after notes", text: "Clear summary of what changed and why it matters." },
      { title: "Next steps", text: "If a rebuild is needed, you get a clear pathway." }
    ],
    stepsHeading: "How It Works",
    steps: [
      { title: "Access", text: "You send the URL and any access details needed." },
      { title: "Assess", text: "We run checks and identify the top fixes." },
      { title: "Implement", text: "We apply practical speed improvements." },
      { title: "Verify", text: "We confirm the improvements and provide notes." }
    ],
    finalHeading: "Want Your Site To Feel Fast On Mobile?",
    finalLead: "Send your link and we will tell you what is slowing it down and what to fix first."
  },

  {
    key: "google-business-profile-setup",
    path: "/google-business-profile-setup",
    title: "Google Business Profile Setup | Local Visibility | 2merative Web Design",
    description:
      "Google Business Profile setup and clean-up. Improve local visibility with correct services, categories, photos, and a simple posting plan.",
    heroHeading: "Google Business Profile Setup That Improves Local Visibility",
    heroSubheading:
      "Your Google profile is often the first thing customers see. We set it up properly so it drives real enquiries.",
    problemHeading: "Most Profiles Are Live But Not Optimised",
    problemParagraphs: [
      "A profile can exist for years and still miss the basics. Wrong categories, weak services, inconsistent details, and no plan for posts or photos.",
      "These gaps reduce trust and make it harder to show up where you should.",
      "We clean it up and align it to your website so the signals match."
    ],
    problemBoxHeading: "Common GBP issues",
    problemBullets: [
      "Wrong primary category",
      "Services incomplete or vague",
      "Business details inconsistent",
      "Weak photos or no recent photos",
      "No posting rhythm",
      "No clear link path to contact or book"
    ],
    offerHeading: "What We Improve",
    offerLead: "Practical improvements that make the profile clearer, more trustworthy, and easier to act on.",
    offerCards: [
      { title: "Categories and services", text: "Correct category selection and a clean service list." },
      { title: "Trust signals", text: "Photos, descriptions, and details that build confidence." },
      { title: "Link path", text: "Clear links to contact, book, and key pages." },
      { title: "Posting starter plan", text: "Simple rhythm and templates you can repeat." },
      { title: "Q&A guidance", text: "Common questions and suggested answers." },
      { title: "Website alignment", text: "Make sure your site and GBP match." }
    ],
    stepsHeading: "How It Works",
    steps: [
      { title: "Quick review", text: "We check categories, services, and consistency." },
      { title: "Fixes", text: "We implement the key updates and align signals." },
      { title: "Content plan", text: "You get a simple photo and post plan." },
      { title: "Ongoing", text: "Optional support if you want it maintained." }
    ],
    finalHeading: "Want Your Profile To Work Harder Locally?",
    finalLead: "Send the link to your profile and your website. We will tell you what is missing and what to fix first."
  },

  // Vertical segments
  {
    key: "websites-for-chiropractors",
    path: "/websites-for-chiropractors",
    title: "Websites for Chiropractors | Calm, Clear, AHPRA-Safe | 2merative Web Design",
    description:
      "Websites for chiropractors built to convert enquiries and bookings. Calm design, clear services, mobile-first, and sensible health wording.",
    heroHeading: "Websites For Chiropractors That Turn Searches Into Bookings",
    heroSubheading:
      "Calm, professional design. Clear services. A simple path to book. Built for local visibility and sensible health wording.",
    problemHeading: "Trust And Clarity Matter More Than Fancy Features",
    problemParagraphs: [
      "Visitors decide quickly if they trust you. If the site is cluttered, unclear, or too claim-heavy, they leave.",
      "Your website should answer the basics fast, explain who you help, and make booking easy.",
      "We build chiro sites that feel calm and professional and guide new clients to action."
    ],
    problemBoxHeading: "Common issues we fix",
    problemBullets: [
      "Visitors cannot tell what you offer within 10 seconds",
      "Booking button is buried or missing on mobile",
      "Services are vague or confusing",
      "No clear new clients flow",
      "Local search visibility is weak",
      "The site feels dated and slow"
    ],
    offerHeading: "What You Get In A Chiro Website Build",
    offerLead: "A site that builds trust, answers questions early, and makes booking simple.",
    offerCards: [
      { title: "New client pathway", text: "Clear flow that explains what to expect and how to book." },
      { title: "Service clarity", text: "Clean pages that explain what it is and who it is for." },
      { title: "Mobile-first booking", text: "Call or book actions that stay obvious on phones." },
      { title: "Local visibility", text: "Structure that supports local search." },
      { title: "Proof and trust", text: "Social proof and credibility signals in the right places." },
      { title: "Fast and calm design", text: "Quick load and readable layout." }
    ],
    stepsHeading: "How We Build It",
    steps: [
      { title: "Discovery", text: "Confirm services, areas, and booking flow." },
      { title: "Plan", text: "Simple structure for Home, New Clients, Services, Contact." },
      { title: "Build", text: "Fast first version, then refine from feedback." },
      { title: "Launch", text: "Go live and test booking paths on mobile." }
    ],
    finalHeading: "Want A Chiro Website That Feels Calm And Converts?",
    finalLead: "Tell us what you offer, where you work, and how you book. We will map the simplest site that improves enquiries."
  },

  {
    key: "websites-for-tradies",
    path: "/websites-for-tradies",
    title: "Websites for Tradies | More Calls, More Quotes | 2merative Web Design",
    description:
      "Websites for tradies built for phone-first enquiries. Fast load, clear services, service areas, and quote-ready calls to action.",
    heroHeading: "Tradie Websites Built To Get Calls And Quote Requests",
    heroSubheading:
      "Simple, fast websites that show what you do, where you work, and how to contact you in one tap.",
    problemHeading: "Most Tradie Sites Lose Enquiries On Mobile",
    problemParagraphs: [
      "Customers search on their phone, compare two or three options, then choose the easiest one to contact.",
      "If your number is hard to find, your services are unclear, or your site is slow, they move on.",
      "We build tradie websites that make calling or requesting a quote the easiest next step."
    ],
    problemBoxHeading: "Signs your site is costing jobs",
    problemBullets: [
      "Your number is not obvious on mobile",
      "No service areas listed",
      "No proof of work or photos",
      "Too much text, not enough clarity",
      "Slow load or messy layout",
      "No clear quote request path"
    ],
    offerHeading: "What You Get With A Tradie Website",
    offerLead: "A website designed to support calls, quotes, and local visibility.",
    offerCards: [
      { title: "Phone-first layout", text: "Tap-to-call and message actions in the right places." },
      { title: "Clear services", text: "Service pages written for real customers." },
      { title: "Service areas", text: "Suburbs and regions listed clearly." },
      { title: "Proof of work", text: "Gallery and highlights that build trust fast." },
      { title: "Fast load", text: "Built to load quickly on mobile data." },
      { title: "Quote-ready CTA", text: "Simple quote request flow that asks only what you need." }
    ],
    stepsHeading: "How We Get It Done",
    steps: [
      { title: "Quick call", text: "Confirm services, areas, and the jobs you want more of." },
      { title: "Page list", text: "Home, Services, Areas, Gallery, About, Contact." },
      { title: "Build fast", text: "First version quickly, then refine from feedback." },
      { title: "Launch", text: "Go live and confirm calls and forms work." }
    ],
    finalHeading: "Want More Calls From People In Your Area?",
    finalLead: "Send your current site link. We will tell you what is costing enquiries and what to fix first."
  },

  {
    key: "websites-for-physios",
    path: "/websites-for-physios",
    title: "Websites for Physios | Trust, Clarity, Bookings | 2merative Web Design",
    description:
      "Physio websites built to support bookings and enquiries. Clear services, mobile-first, and strong local visibility.",
    heroHeading: "Websites For Physios Built For Bookings And Trust",
    heroSubheading:
      "Clear services, calm design, and an easy path to book. Built to support local search and real enquiries.",
    problemHeading: "People Want Confidence Before They Book",
    problemParagraphs: [
      "Visitors want to know what you help with, how appointments work, and how to book quickly.",
      "If the site is unclear or slow, they choose someone else.",
      "We build physio websites that feel professional and make booking easy."
    ],
    problemBoxHeading: "Common friction points",
    problemBullets: [
      "No clear services or conditions list",
      "Booking buttons not obvious on mobile",
      "Old design that feels untrustworthy",
      "Confusing navigation",
      "Weak local search presence",
      "No proof or testimonials"
    ],
    offerHeading: "What You Get",
    offerLead: "A website that answers the basics fast and converts visits into bookings.",
    offerCards: [
      { title: "Service clarity", text: "Clear pages for your key services." },
      { title: "Booking flow", text: "Simple path to book or contact." },
      { title: "Local visibility", text: "Structure that supports local intent search." },
      { title: "Trust signals", text: "Proof, credentials, and testimonials placed well." },
      { title: "Mobile-first design", text: "Easy to read and use on phones." },
      { title: "Fast load", text: "Quick pages that keep people moving." }
    ],
    stepsHeading: "How We Build It",
    steps: [
      { title: "Discovery", text: "Confirm services, areas, and booking system." },
      { title: "Structure", text: "Map the pages that support new clients." },
      { title: "Build", text: "Design, copy support, and implementation." },
      { title: "Launch", text: "Test mobile booking, forms, and tracking." }
    ],
    finalHeading: "Want A Physio Website That Converts?",
    finalLead: "Send your current link and we will suggest the simplest path to more bookings."
  },

  {
    key: "websites-for-dentists",
    path: "/websites-for-dentists",
    title: "Websites for Dentists | Premium Feel, Clear Services | 2merative Web Design",
    description:
      "Dentist websites that build trust and drive enquiries. Clear services, strong local intent, and mobile-first conversion.",
    heroHeading: "Dentist Websites That Build Trust And Drive Enquiries",
    heroSubheading:
      "Premium feel, clear services, and a simple path to call or book. Built for local visibility and trust.",
    problemHeading: "First Impressions Matter More In Dental",
    problemParagraphs: [
      "People are anxious about dental. They need reassurance and clarity.",
      "A dated website or unclear service pages can lose a new patient instantly.",
      "We build dentist sites that feel premium and make contact easy."
    ],
    problemBoxHeading: "Common issues we fix",
    problemBullets: [
      "Outdated design that feels low trust",
      "Services hard to find or unclear",
      "No clear new patient path",
      "No before/after or proof",
      "Mobile contact is awkward",
      "Local visibility is weak"
    ],
    offerHeading: "What You Get",
    offerLead: "A clean site that supports trust, services, and easy bookings.",
    offerCards: [
      { title: "Service pages", text: "Clear pages for your main treatments." },
      { title: "New patient flow", text: "Explain what happens and how to start." },
      { title: "Trust and proof", text: "Testimonials and credibility signals placed well." },
      { title: "Mobile-first contact", text: "Call and booking actions always visible." },
      { title: "Local intent structure", text: "Built to support suburb and service search." },
      { title: "Fast load", text: "Smooth, quick pages that feel premium." }
    ],
    stepsHeading: "How We Build It",
    steps: [
      { title: "Discovery", text: "Confirm services, tone, and practice priorities." },
      { title: "Plan", text: "Map pages and conversion path." },
      { title: "Build", text: "Design and implement with feedback loops." },
      { title: "Launch", text: "Test contact paths and mobile performance." }
    ],
    finalHeading: "Want A Dentist Website That Feels Premium And Converts?",
    finalLead: "Send your link and we will map the simplest upgrade path."
  },

  // Business-type segments
  {
    key: "websites-for-restaurants",
    path: "/websites-for-restaurants",
    title: "Restaurant Websites | Menus, Bookings, Calls | 2merative Web Design",
    description:
      "Restaurant websites built to convert. Fast menus, clear hours, booking links, and mobile-first calls to action.",
    heroHeading: "Restaurant Websites That Make It Easy To Book And Order",
    heroSubheading:
      "Fast menus, clear hours, and one-tap calls. Built for mobile customers searching right now.",
    problemHeading: "Most Restaurant Sites Make Menus Hard To Use",
    problemParagraphs: [
      "If the menu is slow, tiny, or hidden in a PDF, people bounce.",
      "Customers want hours, location, menu, and booking fast.",
      "We build restaurant sites that remove friction and drive action."
    ],
    problemBoxHeading: "Common friction points",
    problemBullets: [
      "Menu is a slow PDF",
      "Hours and location unclear",
      "No obvious booking link",
      "Too slow on mobile data",
      "No Google alignment",
      "No clear calls to action"
    ],
    offerHeading: "What You Get",
    offerLead: "A fast restaurant site that makes the next step easy.",
    offerCards: [
      { title: "Fast menu layout", text: "Readable menu built for phones." },
      { title: "Booking and calls", text: "One-tap booking and calling." },
      { title: "Clear essentials", text: "Hours, location, parking, and contacts." },
      { title: "Local search support", text: "Structure that supports local discovery." },
      { title: "Photos that load fast", text: "Great visuals without slow load." },
      { title: "Events and specials", text: "Easy space for updates and promos." }
    ],
    stepsHeading: "How We Build It",
    steps: [
      { title: "Discovery", text: "Confirm menu, booking, and key info." },
      { title: "Structure", text: "Map a simple page flow customers want." },
      { title: "Build", text: "Implement fast and mobile-first." },
      { title: "Launch", text: "Test on phones and fix any friction." }
    ],
    finalHeading: "Want A Restaurant Website That Gets More Bookings?",
    finalLead: "Send your current link and we will identify what is slowing customers down."
  },

  {
    key: "websites-for-cafes",
    path: "/websites-for-cafes",
    title: "Cafe Websites | Menus, Hours, Local Visibility | 2merative Web Design",
    description:
      "Cafe websites built for local customers. Clear hours, menu highlights, and one-tap directions and calls.",
    heroHeading: "Cafe Websites Built For Local Customers On Their Phone",
    heroSubheading:
      "Clear hours, menu highlights, and one-tap calls and directions. Built to support local discovery.",
    problemHeading: "People Decide Where To Go In Seconds",
    problemParagraphs: [
      "Customers want to know if you are open, where you are, and what you serve.",
      "If they cannot find that quickly, they go elsewhere.",
      "We build cafe websites that make those answers instant."
    ],
    problemBoxHeading: "Common issues we fix",
    problemBullets: [
      "Hours missing or incorrect",
      "No clear menu highlights",
      "Location hard to find",
      "Slow load on mobile",
      "No call to action",
      "GBP and website not aligned"
    ],
    offerHeading: "What You Get",
    offerLead: "A fast cafe site that supports calls, directions, and local visibility.",
    offerCards: [
      { title: "Clear essentials", text: "Hours, location, contact, parking info." },
      { title: "Menu highlights", text: "Readable, simple menu layout." },
      { title: "One-tap actions", text: "Call and directions always easy." },
      { title: "Local visibility", text: "Structure that supports local search." },
      { title: "Fast photos", text: "Strong visuals without slow load." },
      { title: "Updates", text: "Easy space for specials and events." }
    ],
    stepsHeading: "How We Build It",
    steps: [
      { title: "Discovery", text: "Confirm essentials and priorities." },
      { title: "Structure", text: "Map pages and sections." },
      { title: "Build", text: "Implement mobile-first design." },
      { title: "Launch", text: "Test on phones and refine." }
    ],
    finalHeading: "Want A Cafe Website That Makes It Easy To Visit You?",
    finalLead: "Send your link and we will map what to fix first."
  },

  {
    key: "websites-for-gyms",
    path: "/websites-for-gyms",
    title: "Gym Websites | Membership Enquiries | 2merative Web Design",
    description:
      "Gym websites built to convert. Clear offers, timetable, and a simple path to trial, call, or join.",
    heroHeading: "Gym Websites That Convert Trials And Membership Enquiries",
    heroSubheading:
      "Clear offers, timetable, and an easy path to start. Built for mobile-first signups and enquiries.",
    problemHeading: "Confusion Kills Signups",
    problemParagraphs: [
      "If people cannot tell what you offer, what it costs, or how to start, they leave.",
      "Most gym websites bury timetables and offers.",
      "We build gym sites that make your next step obvious."
    ],
    problemBoxHeading: "Common friction points",
    problemBullets: [
      "No clear offer or trial",
      "Timetable hard to find",
      "Pricing unclear",
      "No proof or testimonials",
      "Mobile layout is messy",
      "No fast enquiry path"
    ],
    offerHeading: "What You Get",
    offerLead: "A site that supports trials, enquiries, and signups.",
    offerCards: [
      { title: "Clear offer", text: "Trial or offer positioned properly." },
      { title: "Timetable clarity", text: "Classes and times easy to find." },
      { title: "Membership pathway", text: "Simple path to join or enquire." },
      { title: "Trust and proof", text: "Testimonials and results framing." },
      { title: "Mobile-first CTA", text: "Call, enquire, or join in one tap." },
      { title: "Fast load", text: "Quick pages that keep people moving." }
    ],
    stepsHeading: "How We Build It",
    steps: [
      { title: "Discovery", text: "Confirm offer, timetable, and CTA." },
      { title: "Plan", text: "Map the conversion path." },
      { title: "Build", text: "Implement and refine." },
      { title: "Launch", text: "Test and confirm enquiry flows." }
    ],
    finalHeading: "Want More Trials And Enquiries?",
    finalLead: "Send your current link and we will map the simplest upgrade path."
  },

  {
    key: "websites-for-accountants",
    path: "/websites-for-accountants",
    title: "Accountant Websites | Trust, Clarity, Leads | 2merative Web Design",
    description:
      "Accountant websites built to generate enquiries. Clear services, trust signals, and a clean conversion path.",
    heroHeading: "Accountant Websites Built To Generate Quality Enquiries",
    heroSubheading:
      "Clear services, trust signals, and a simple path to contact. Built for local visibility and credibility.",
    problemHeading: "Professional Services Need Proof And Simplicity",
    problemParagraphs: [
      "Visitors want to know you are credible, what you do, and how to start.",
      "A confusing site can lose a high-value lead quickly.",
      "We build accountant sites that feel established and convert enquiries."
    ],
    problemBoxHeading: "Common issues we fix",
    problemBullets: [
      "Services unclear or buried",
      "No clear next step for prospects",
      "Trust and proof missing",
      "Site looks dated",
      "Mobile experience is weak",
      "Local discovery is poor"
    ],
    offerHeading: "What You Get",
    offerLead: "A site that builds confidence and drives enquiries.",
    offerCards: [
      { title: "Service clarity", text: "Clear pages for your key services." },
      { title: "Trust signals", text: "Proof, credentials, and reviews." },
      { title: "Lead pathway", text: "Simple enquiry path that works on mobile." },
      { title: "Local intent support", text: "Structure built for local search." },
      { title: "Fast load", text: "Quick pages that feel professional." },
      { title: "Simple updates", text: "Clear content structure for future changes." }
    ],
    stepsHeading: "How We Build It",
    steps: [
      { title: "Discovery", text: "Confirm services and target clients." },
      { title: "Plan", text: "Map pages and conversion flow." },
      { title: "Build", text: "Implement design and content structure." },
      { title: "Launch", text: "Test forms, calls, and tracking." }
    ],
    finalHeading: "Want More Quality Enquiries?",
    finalLead: "Send your site link and we will map the simplest improvements first."
  },
  {
  key: "websites-for-accounting-firms",
  path: "/websites-for-accounting-firms",
  title: "Websites for Accounting Firms | Trust, Clarity, Leads | 2merative Web Design",
  description:
    "Websites for accounting firms built to generate quality enquiries. Clear services, strong trust signals, and a simple conversion path.",
  heroHeading: "Websites For Accounting Firms That Generate Quality Enquiries",
  heroSubheading:
    "Clear services, trust signals, and a simple path to contact. Built to support local visibility and credibility.",
  problemHeading: "Professional Services Need Proof And Simplicity",
  problemParagraphs: [
    "Visitors want to know you are credible, what you do, and how to start.",
    "A confusing site can lose a high-value lead quickly.",
    "We build accounting firm sites that feel established and convert enquiries."
  ],
  problemBoxHeading: "Common issues we fix",
  problemBullets: [
    "Services unclear or buried",
    "No clear next step for prospects",
    "Trust and proof missing",
    "Site looks dated",
    "Mobile experience is weak",
    "Local discovery is poor"
  ],
  offerHeading: "What You Get",
  offerLead: "A site that builds confidence and drives enquiries.",
  offerCards: [
    { title: "Service clarity", text: "Clear pages for tax, BAS, bookkeeping, and advisory." },
    { title: "Trust signals", text: "Credentials, reviews, and proof placed properly." },
    { title: "Lead pathway", text: "A simple enquiry flow that works on mobile." },
    { title: "Local intent support", text: "Structure built for local suburb searches." },
    { title: "Fast load", text: "Quick pages that feel professional." },
    { title: "Simple updates", text: "Clean content layout for future changes." }
  ],
  stepsHeading: "How It Works",
  steps: [
    { title: "Discovery", text: "Confirm services, ideal clients, and key offers." },
    { title: "Structure", text: "Map pages and a clean enquiry pathway." },
    { title: "Build", text: "Design, copy support, and implementation." },
    { title: "Launch", text: "Test mobile contact paths and tracking." }
  ],
  finalHeading: "Want More Enquiries From Better Clients?",
  finalLead: "Send your site link and we will map the simplest improvements first."
},

{
  key: "websites-for-lawyers",
  path: "/websites-for-lawyers",
  title: "Websites for Lawyers | Credibility, Clarity, Enquiries | 2merative Web Design",
  description:
    "Websites for lawyers built to convert enquiries. Clear practice areas, credibility signals, and a simple path to contact.",
  heroHeading: "Websites For Lawyers Built For Credibility And Enquiries",
  heroSubheading:
    "Clear practice areas, strong trust signals, and a simple path to contact. Built for local visibility and real enquiries.",
  problemHeading: "If It Looks Unclear, People Leave",
  problemParagraphs: [
    "Legal clients want confidence fast. They compare options quickly and choose the easiest firm to understand and contact.",
    "If your practice areas are vague or your site looks dated, you lose enquiries before they call.",
    "We build lawyer sites that feel credible and make next steps clear."
  ],
  problemBoxHeading: "Common friction points",
  problemBullets: [
    "Practice areas are unclear",
    "No strong credibility signals",
    "No clear call or enquiry path",
    "Mobile layout is hard to use",
    "Site feels dated or slow",
    "Local visibility is weak"
  ],
  offerHeading: "What You Get",
  offerLead: "A credible website that makes it simple to enquire.",
  offerCards: [
    { title: "Practice area clarity", text: "Clear pages for each service with plain language structure." },
    { title: "Trust signals", text: "Accreditations, memberships, reviews, and proof placed well." },
    { title: "Enquiry path", text: "Simple contact flow with the right fields only." },
    { title: "Mobile-first", text: "Easy to call, enquire, and read on phones." },
    { title: "Local SEO structure", text: "Built to support suburb and service searches." },
    { title: "Fast performance", text: "Quick load times that keep people engaged." }
  ],
  stepsHeading: "How It Works",
  steps: [
    { title: "Discovery", text: "Confirm practice areas, tone, and target clients." },
    { title: "Structure", text: "Map pages and conversion flow." },
    { title: "Build", text: "Design and implement with feedback loops." },
    { title: "Launch", text: "Test contact, tracking, and mobile usability." }
  ],
  finalHeading: "Want A Lawyer Website That Converts More Enquiries?",
  finalLead: "Send your current link and we will map the cleanest upgrade path."
},

{
  key: "websites-for-financial-advisers",
  path: "/websites-for-financial-advisers",
  title: "Websites for Financial Advisers | Trust, Leads, Clarity | 2merative Web Design",
  description:
    "Websites for financial advisers built to generate enquiries. Clear services, trust signals, and a simple path to book a call.",
  heroHeading: "Websites For Financial Advisers Built For Trust And Enquiries",
  heroSubheading:
    "Clear services, credibility signals, and a simple path to book a call. Built for local visibility and high-trust clients.",
  problemHeading: "People Need Confidence Before They Book",
  problemParagraphs: [
    "Financial decisions are high-trust. Visitors want clear services and strong proof.",
    "If your site is confusing or salesy, people bounce.",
    "We build adviser sites that feel calm, credible, and easy to act on."
  ],
  problemBoxHeading: "Common issues we fix",
  problemBullets: [
    "Services unclear or too broad",
    "No strong trust signals",
    "No clear discovery call path",
    "Mobile experience is weak",
    "Site feels dated",
    "Local intent visibility is low"
  ],
  offerHeading: "What You Get",
  offerLead: "A website that builds confidence and drives qualified enquiries.",
  offerCards: [
    { title: "Service clarity", text: "Clear pages for super, retirement, insurance, and advice areas." },
    { title: "Trust signals", text: "Credentials, memberships, and proof in the right places." },
    { title: "Book a call flow", text: "Simple conversion path to schedule or enquire." },
    { title: "Mobile-first design", text: "Readable and easy to use on phones." },
    { title: "Local visibility structure", text: "Built to support local and niche searches." },
    { title: "Fast load", text: "Performance that feels premium." }
  ],
  stepsHeading: "How It Works",
  steps: [
    { title: "Discovery", text: "Confirm services, ideal clients, and compliance tone." },
    { title: "Structure", text: "Map pages and enquiry pathway." },
    { title: "Build", text: "Design, implement, refine." },
    { title: "Launch", text: "Test conversion flow and mobile usability." }
  ],
  finalHeading: "Want Better Enquiries From The Right Clients?",
  finalLead: "Send your site link and we will map the simplest improvements first."
},

{
  key: "websites-for-real-estate-agents",
  path: "/websites-for-real-estate-agents",
  title: "Websites for Real Estate Agents | Listings, Leads, Trust | 2merative Web Design",
  description:
    "Websites for real estate agents built to generate listings and enquiries. Clear services, proof, and fast mobile contact paths.",
  heroHeading: "Websites For Real Estate Agents That Win More Enquiries",
  heroSubheading:
    "Clear services, proof, and fast mobile contact paths. Built to support local visibility and appraisal requests.",
  problemHeading: "If Your Site Looks Generic, You Lose Listings",
  problemParagraphs: [
    "Vendors compare agents fast. They choose the agent who looks credible and easy to contact.",
    "A generic site with weak proof can cost you appraisal enquiries.",
    "We build real estate sites that highlight proof and drive calls."
  ],
  problemBoxHeading: "Common friction points",
  problemBullets: [
    "No clear reason to choose you",
    "Proof and results are buried",
    "Appraisal CTA is unclear",
    "Mobile calling is not obvious",
    "Site loads slowly",
    "Local suburb intent is weak"
  ],
  offerHeading: "What You Get",
  offerLead: "A website that supports listings, trust, and enquiries.",
  offerCards: [
    { title: "Appraisal pathway", text: "Clear CTA and flow to request an appraisal." },
    { title: "Proof and results", text: "Sold stats, testimonials, and recent wins presented well." },
    { title: "Local suburb structure", text: "Support local suburb visibility and credibility." },
    { title: "Mobile-first contact", text: "Tap-to-call and fast enquiry actions." },
    { title: "Listings integration-ready", text: "Works with your preferred listings platform approach." },
    { title: "Fast load", text: "Quick pages that feel premium." }
  ],
  stepsHeading: "How It Works",
  steps: [
    { title: "Discovery", text: "Confirm your service area and key proof assets." },
    { title: "Structure", text: "Map the appraisal and enquiry flow." },
    { title: "Build", text: "Design and implement for conversion." },
    { title: "Launch", text: "Test mobile calls and lead forms." }
  ],
  finalHeading: "Want More Appraisal Enquiries?",
  finalLead: "Send your current site link and we will map the simplest upgrade path."
},

{
  key: "websites-for-builders",
  path: "/websites-for-builders",
  title: "Websites for Builders | Trust, Projects, Enquiries | 2merative Web Design",
  description:
    "Websites for builders built to win better jobs. Clear services, strong project proof, and an easy path to request a quote.",
  heroHeading: "Websites For Builders That Win Better Enquiries",
  heroSubheading:
    "Clear services, strong project proof, and a simple quote pathway. Built for mobile-first customers searching locally.",
  problemHeading: "People Want Proof Before They Call",
  problemParagraphs: [
    "Builders win work with trust and proof. Customers want to see quality fast.",
    "If your projects are hard to view on mobile, you lose quote requests.",
    "We build builder sites that show proof and make it easy to enquire."
  ],
  problemBoxHeading: "Common issues we fix",
  problemBullets: [
    "Projects are buried or hard to browse",
    "Services are unclear",
    "No clear quote request path",
    "Mobile layout is messy",
    "Site loads slowly",
    "No service areas listed"
  ],
  offerHeading: "What You Get",
  offerLead: "A builder website that shows proof and drives quote requests.",
  offerCards: [
    { title: "Project showcase", text: "A clean gallery that works on mobile." },
    { title: "Clear services", text: "Service pages that match how customers search." },
    { title: "Quote pathway", text: "Simple enquiry form that captures the essentials." },
    { title: "Service areas", text: "Suburbs and regions listed clearly." },
    { title: "Trust signals", text: "Reviews, licences, and proof placed well." },
    { title: "Fast load", text: "Performance that feels professional." }
  ],
  stepsHeading: "How It Works",
  steps: [
    { title: "Discovery", text: "Confirm services, areas, and the jobs you want." },
    { title: "Structure", text: "Map pages and proof assets." },
    { title: "Build", text: "Implement with fast feedback loops." },
    { title: "Launch", text: "Test mobile enquiries and calls." }
  ],
  finalHeading: "Want Better Quote Requests?",
  finalLead: "Send your current site link and we will map the quickest improvements."
},

{
  key: "websites-for-plumbers",
  path: "/websites-for-plumbers",
  title: "Websites for Plumbers | More Calls, More Jobs | 2merative Web Design",
  description:
    "Websites for plumbers built for phone-first calls and local search. Clear services, service areas, and fast quote requests.",
  heroHeading: "Plumber Websites Built To Get Calls Fast",
  heroSubheading:
    "Phone-first layout, clear services, and fast contact paths. Built for local customers searching right now.",
  problemHeading: "Plumbing Leads Go To The Easiest Business To Contact",
  problemParagraphs: [
    "Most plumbing searches happen on a phone and the customer wants a quick solution.",
    "If your number is not obvious or your site is slow, they choose the next plumber.",
    "We build plumber sites that make calling and booking easy."
  ],
  problemBoxHeading: "Common issues we fix",
  problemBullets: [
    "Phone number not obvious on mobile",
    "No clear emergency or priority service messaging",
    "Service areas missing",
    "No proof or reviews shown",
    "Slow load times",
    "Confusing service list"
  ],
  offerHeading: "What You Get",
  offerLead: "A plumber website designed for calls, trust, and local visibility.",
  offerCards: [
    { title: "Phone-first CTA", text: "Tap-to-call is obvious and easy." },
    { title: "Service clarity", text: "Clear services customers recognise." },
    { title: "Service areas", text: "Suburbs and regions listed clearly." },
    { title: "Trust and proof", text: "Reviews and proof where people look." },
    { title: "Fast load", text: "Quick pages that work on mobile data." },
    { title: "Quote request", text: "Simple form that captures essentials." }
  ],
  stepsHeading: "How It Works",
  steps: [
    { title: "Quick chat", text: "Confirm services, areas, and priorities." },
    { title: "Structure", text: "Map your main pages and CTAs." },
    { title: "Build", text: "Implement and refine." },
    { title: "Launch", text: "Test calls and forms on mobile." }
  ],
  finalHeading: "Want More Calls From Local Customers?",
  finalLead: "Send your current site link and we will map the simplest upgrade path."
},

{
  key: "websites-for-electricians",
  path: "/websites-for-electricians",
  title: "Websites for Electricians | More Calls, More Quotes | 2merative Web Design",
  description:
    "Websites for electricians built to generate calls and quote requests. Clear services, strong proof, and local visibility.",
  heroHeading: "Electrician Websites Built To Win More Local Jobs",
  heroSubheading:
    "Clear services, strong proof, and fast mobile contact. Built for customers searching in your area.",
  problemHeading: "People Choose The Clearest Option",
  problemParagraphs: [
    "Electrical customers want confidence. They look for licensing, reviews, and clear services.",
    "If your site looks unclear or hard to contact, they move on.",
    "We build electrician sites that feel trustworthy and make enquiries easy."
  ],
  problemBoxHeading: "Common issues we fix",
  problemBullets: [
    "Services unclear or too broad",
    "No licence or trust signals shown",
    "Quote request is awkward",
    "Mobile calling is not obvious",
    "Projects and proof missing",
    "Local discovery is weak"
  ],
  offerHeading: "What You Get",
  offerLead: "A site designed for calls, quotes, and local visibility.",
  offerCards: [
    { title: "Clear services", text: "Pages that match customer search intent." },
    { title: "Trust signals", text: "Licence and proof positioned well." },
    { title: "Fast contact path", text: "Tap-to-call and simple enquiry flow." },
    { title: "Service areas", text: "Suburbs and regions listed clearly." },
    { title: "Project proof", text: "Gallery and highlights that build trust." },
    { title: "Fast load", text: "Quick pages that feel professional." }
  ],
  stepsHeading: "How It Works",
  steps: [
    { title: "Discovery", text: "Confirm services, areas, and priorities." },
    { title: "Structure", text: "Map pages and CTAs." },
    { title: "Build", text: "Implement and refine quickly." },
    { title: "Launch", text: "Test mobile calls and enquiry flow." }
  ],
  finalHeading: "Want More Quote Requests?",
  finalLead: "Send your current link and we will map the simplest improvements first."
},

{
  key: "websites-for-landscapers",
  path: "/websites-for-landscapers",
  title: "Websites for Landscapers | Projects, Trust, Enquiries | 2merative Web Design",
  description:
    "Websites for landscapers built to win better jobs. Clear services, strong project proof, and an easy quote request path.",
  heroHeading: "Landscaper Websites Built To Win Better Enquiries",
  heroSubheading:
    "Clear services, strong project proof, and a simple path to request a quote. Built for mobile-first customers.",
  problemHeading: "Your Work Sells The Job If People Can See It",
  problemParagraphs: [
    "Landscaping is visual. Customers want to see before and after proof fast.",
    "If your gallery is messy or slow on mobile, you lose quote requests.",
    "We build landscaper sites that showcase projects and make it easy to enquire."
  ],
  problemBoxHeading: "Common issues we fix",
  problemBullets: [
    "Gallery is hard to browse on mobile",
    "Services unclear",
    "No quote request pathway",
    "No service areas listed",
    "Site is slow",
    "No trust signals or reviews"
  ],
  offerHeading: "What You Get",
  offerLead: "A landscaper website that shows proof and drives quote requests.",
  offerCards: [
    { title: "Project gallery", text: "A clean showcase that works on phones." },
    { title: "Clear services", text: "Service pages customers understand." },
    { title: "Quote pathway", text: "Simple enquiry form with the right fields." },
    { title: "Service areas", text: "Suburbs and regions listed clearly." },
    { title: "Trust signals", text: "Reviews and proof placed well." },
    { title: "Fast load", text: "Quick pages for better conversions." }
  ],
  stepsHeading: "How It Works",
  steps: [
    { title: "Discovery", text: "Confirm services, areas, and proof assets." },
    { title: "Structure", text: "Map the pages and quote pathway." },
    { title: "Build", text: "Implement and refine." },
    { title: "Launch", text: "Test mobile enquiries and gallery performance." }
  ],
  finalHeading: "Want Better Quote Requests From Local Customers?",
  finalLead: "Send your site link and we will map the quickest improvements."
},

{
  key: "websites-for-cleaners",
  path: "/websites-for-cleaners",
  title: "Websites for Cleaners | More Bookings, Less Chasing | 2merative Web Design",
  description:
    "Websites for cleaners built for bookings. Clear services, service areas, trust signals, and simple enquiry paths.",
  heroHeading: "Cleaner Websites Built To Get More Bookings",
  heroSubheading:
    "Clear services, trust signals, and simple enquiry paths. Built for local customers who want a quick booking.",
  problemHeading: "If Booking Feels Hard, People Move On",
  problemParagraphs: [
    "Cleaning customers want a quick answer. What do you clean, what areas do you cover, and how do they book.",
    "If your site is unclear, you get low-quality enquiries and wasted calls.",
    "We build cleaner sites that make booking simple and expectations clear."
  ],
  problemBoxHeading: "Common issues we fix",
  problemBullets: [
    "Services unclear or too broad",
    "No service areas listed",
    "No proof or reviews",
    "Enquiry forms ask too much",
    "Mobile layout is messy",
    "Site feels dated"
  ],
  offerHeading: "What You Get",
  offerLead: "A cleaner website designed for bookings and trust.",
  offerCards: [
    { title: "Service clarity", text: "Clear pages for the jobs you want." },
    { title: "Areas covered", text: "Suburbs and regions listed clearly." },
    { title: "Trust signals", text: "Reviews and proof positioned well." },
    { title: "Booking path", text: "Simple form that captures essentials." },
    { title: "Mobile-first", text: "Easy to read and use on phones." },
    { title: "Fast load", text: "Quick pages that convert better." }
  ],
  stepsHeading: "How It Works",
  steps: [
    { title: "Discovery", text: "Confirm services, areas, and booking method." },
    { title: "Structure", text: "Map pages and enquiry flow." },
    { title: "Build", text: "Implement and refine." },
    { title: "Launch", text: "Test enquiries and mobile usability." }
  ],
  finalHeading: "Want More Bookings From Local Customers?",
  finalLead: "Send your current link and we will map the simplest upgrade path."
},
{
  key: "websites-for-dentists",
  path: "/websites-for-dentists",
  title: "Websites for Dentists | More Bookings, More Trust | 2merative Web Design",
  description:
    "Websites for dentists built to generate bookings. Clear services, strong trust signals, and an easy path to call or book.",
  heroHeading: "Websites For Dentists Built To Drive Bookings",
  heroSubheading:
    "Clear services, credibility signals, and a simple booking pathway. Built for local discovery and mobile-first patients.",
  problemHeading: "Patients Choose The Clearest, Most Trustworthy Option",
  problemParagraphs: [
    "Most patients compare quickly. If your site feels unclear or dated, they move on.",
    "Dental websites need calm trust, clear services, and an obvious next step.",
    "We build dentist sites that feel modern and make booking easy."
  ],
  problemBoxHeading: "Common issues we fix",
  problemBullets: [
    "Services are unclear",
    "No strong trust signals",
    "Booking path is hard to find",
    "Mobile experience is weak",
    "Site feels dated or slow",
    "Local visibility is poor"
  ],
  offerHeading: "What You Get",
  offerLead: "A dentist website designed for trust and bookings.",
  offerCards: [
    { title: "Service clarity", text: "Clear pages for key treatments and patient intent." },
    { title: "Trust signals", text: "Reviews, credentials, and proof placed properly." },
    { title: "Booking pathway", text: "Simple call and booking actions on every page." },
    { title: "Mobile-first design", text: "Easy to read and act on from a phone." },
    { title: "Local SEO structure", text: "Built to support suburb and service searches." },
    { title: "Fast load", text: "Quick pages that keep patients engaged." }
  ],
  stepsHeading: "How It Works",
  steps: [
    { title: "Discovery", text: "Confirm services, patients, and tone." },
    { title: "Structure", text: "Map pages and booking flow." },
    { title: "Build", text: "Design and implement with feedback." },
    { title: "Launch", text: "Test mobile booking and contact paths." }
  ],
  finalHeading: "Want More Patient Bookings?",
  finalLead: "Send your current link and we will map the simplest improvements first."
},

{
  key: "websites-for-physiotherapists",
  path: "/websites-for-physiotherapists",
  title: "Websites for Physiotherapists | Bookings, Trust, Clarity | 2merative Web Design",
  description:
    "Websites for physiotherapists built to generate bookings. Clear services, trust signals, and a simple path to enquire.",
  heroHeading: "Websites For Physiotherapists Built To Increase Bookings",
  heroSubheading:
    "Clear services, proof, and an easy booking pathway. Built for local searches and mobile-first patients.",
  problemHeading: "If Patients Cannot Understand Your Services, They Leave",
  problemParagraphs: [
    "People search by pain and outcomes. Your site must match how they think.",
    "A generic services list and weak trust signals reduces bookings.",
    "We build physio sites that guide patients to the right next step."
  ],
  problemBoxHeading: "Common issues we fix",
  problemBullets: [
    "Services list is vague",
    "No clear booking pathway",
    "Trust and proof missing",
    "Mobile site is hard to use",
    "No local service area clarity",
    "Pages feel slow or dated"
  ],
  offerHeading: "What You Get",
  offerLead: "A physio website built for local visibility and bookings.",
  offerCards: [
    { title: "Service clarity", text: "Clear services and patient intent alignment." },
    { title: "Trust signals", text: "Qualifications, reviews, and proof placed well." },
    { title: "Booking pathway", text: "Simple calls to action that work on mobile." },
    { title: "Local SEO structure", text: "Support local suburb and condition searches." },
    { title: "Fast load", text: "Quick pages that feel professional." },
    { title: "Simple updates", text: "Clean structure that stays easy to maintain." }
  ],
  stepsHeading: "How It Works",
  steps: [
    { title: "Discovery", text: "Confirm services, patients, and main booking flow." },
    { title: "Structure", text: "Map pages and CTA placements." },
    { title: "Build", text: "Implement and refine." },
    { title: "Launch", text: "Test mobile enquiry and booking." }
  ],
  finalHeading: "Want More Bookings From Local Patients?",
  finalLead: "Send your current link and we will map the cleanest upgrade path."
},

{
  key: "websites-for-psychologists",
  path: "/websites-for-psychologists",
  title: "Websites for Psychologists | Calm, Clear, Bookings | 2merative Web Design",
  description:
    "Websites for psychologists built for clarity and bookings. Calm design, clear services, and a simple enquiry path.",
  heroHeading: "Websites For Psychologists Built For Calm Trust And Bookings",
  heroSubheading:
    "Clear services, safe tone, and a simple enquiry pathway. Built for local discovery and mobile-first visitors.",
  problemHeading: "People Need Safety And Clarity Before They Reach Out",
  problemParagraphs: [
    "Visitors are often anxious and overwhelmed. Your site must feel calm and easy to understand.",
    "If the next step is unclear, they will not contact you.",
    "We build psychology sites that reduce friction and increase enquiries."
  ],
  problemBoxHeading: "Common issues we fix",
  problemBullets: [
    "Tone feels cold or confusing",
    "Services are unclear",
    "No clear enquiry pathway",
    "Mobile layout is hard to read",
    "Trust signals are missing",
    "Site feels dated"
  ],
  offerHeading: "What You Get",
  offerLead: "A calm, clear website designed to increase enquiries.",
  offerCards: [
    { title: "Clear service pages", text: "Present support areas in simple language." },
    { title: "Trust signals", text: "Credentials and proof positioned carefully." },
    { title: "Easy enquiry flow", text: "Simple contact path with the right prompts." },
    { title: "Mobile-first", text: "Readable, calm layout on phones." },
    { title: "Local visibility", text: "Structure to support local searches." },
    { title: "Fast load", text: "Quick pages that feel smooth." }
  ],
  stepsHeading: "How It Works",
  steps: [
    { title: "Discovery", text: "Confirm services, tone, and enquiry flow." },
    { title: "Structure", text: "Map pages and key content blocks." },
    { title: "Build", text: "Implement design and content layout." },
    { title: "Launch", text: "Test contact path and mobile usability." }
  ],
  finalHeading: "Want More Enquiries From The Right Clients?",
  finalLead: "Send your current link and we will map the simplest improvements first."
},

{
  key: "websites-for-occupational-therapists",
  path: "/websites-for-occupational-therapists",
  title: "Websites for Occupational Therapists | Referrals and Enquiries | 2merative Web Design",
  description:
    "Websites for occupational therapists built to support referrals and enquiries. Clear services, trust, and easy contact paths.",
  heroHeading: "Websites For Occupational Therapists Built For Referrals And Enquiries",
  heroSubheading:
    "Clear services, credibility signals, and a simple enquiry path. Built for local searches and mobile-first visitors.",
  problemHeading: "Referrers And Families Need Clarity Fast",
  problemParagraphs: [
    "OT clients and referrers want to understand your services quickly.",
    "If your offerings are unclear, you lose referrals and enquiries.",
    "We build OT sites that explain services clearly and make next steps easy."
  ],
  problemBoxHeading: "Common issues we fix",
  problemBullets: [
    "Services unclear or too broad",
    "Referrals info missing",
    "No clear enquiry path",
    "Mobile pages hard to use",
    "Trust signals missing",
    "Local visibility weak"
  ],
  offerHeading: "What You Get",
  offerLead: "A website built to support trust, referrals, and bookings.",
  offerCards: [
    { title: "Service clarity", text: "Clear pages for key OT services and audiences." },
    { title: "Referral support", text: "Simple referral information and pathways." },
    { title: "Trust signals", text: "Credentials and proof placed well." },
    { title: "Mobile-first", text: "Fast, readable layout on phones." },
    { title: "Local SEO structure", text: "Support local intent and service searches." },
    { title: "Clear CTAs", text: "Simple next steps for families and referrers." }
  ],
  stepsHeading: "How It Works",
  steps: [
    { title: "Discovery", text: "Confirm services and target audiences." },
    { title: "Structure", text: "Map pages and referral flow." },
    { title: "Build", text: "Implement with feedback." },
    { title: "Launch", text: "Test enquiries on mobile." }
  ],
  finalHeading: "Want Better Referrals And Enquiries?",
  finalLead: "Send your current link and we will map the cleanest improvements first."
},

{
  key: "websites-for-speech-pathologists",
  path: "/websites-for-speech-pathologists",
  title: "Websites for Speech Pathologists | Referrals and Bookings | 2merative Web Design",
  description:
    "Websites for speech pathologists built to support referrals and bookings. Clear services, trust signals, and easy enquiry paths.",
  heroHeading: "Websites For Speech Pathologists Built For Referrals And Bookings",
  heroSubheading:
    "Clear services for families and referrers, trust signals, and a simple booking pathway. Built for mobile-first visitors.",
  problemHeading: "Families Need Clear Help, Not More Confusion",
  problemParagraphs: [
    "Parents and carers want to know you can help and how to start.",
    "If your services and process are unclear, they delay or go elsewhere.",
    "We build speech pathology sites that guide people to the next step."
  ],
  problemBoxHeading: "Common issues we fix",
  problemBullets: [
    "Services unclear for families",
    "No referral pathway info",
    "Weak trust signals",
    "Mobile pages hard to read",
    "No clear next step",
    "Local visibility weak"
  ],
  offerHeading: "What You Get",
  offerLead: "A website built for clarity, trust, and bookings.",
  offerCards: [
    { title: "Service clarity", text: "Explain support areas in simple language." },
    { title: "Referral support", text: "Clear info for GPs and referrers." },
    { title: "Trust signals", text: "Credentials and proof placed well." },
    { title: "Booking pathway", text: "Clear enquiry and booking calls to action." },
    { title: "Mobile-first", text: "Readable on phones without friction." },
    { title: "Local SEO structure", text: "Support local service searches." }
  ],
  stepsHeading: "How It Works",
  steps: [
    { title: "Discovery", text: "Confirm services, audiences, and booking flow." },
    { title: "Structure", text: "Map pages and referral blocks." },
    { title: "Build", text: "Implement and refine." },
    { title: "Launch", text: "Test mobile enquiries and forms." }
  ],
  finalHeading: "Want More Referrals And Bookings?",
  finalLead: "Send your current link and we will map the simplest upgrades first."
},

{
  key: "websites-for-veterinarians",
  path: "/websites-for-veterinarians",
  title: "Websites for Vets | Bookings, Trust, Local Visibility | 2merative Web Design",
  description:
    "Websites for veterinarians built for bookings and trust. Clear services, emergency messaging, and fast mobile contact paths.",
  heroHeading: "Websites For Vets Built To Increase Bookings",
  heroSubheading:
    "Clear services, trust signals, and fast mobile contact. Built for local pet owners searching right now.",
  problemHeading: "Pet Owners Choose The Easiest Clinic To Contact",
  problemParagraphs: [
    "Most pet owners search on mobile and want quick answers.",
    "If your phone number and booking path are not obvious, they choose another clinic.",
    "We build vet sites that make booking easy and trust clear."
  ],
  problemBoxHeading: "Common issues we fix",
  problemBullets: [
    "Phone number not obvious on mobile",
    "No clear booking pathway",
    "Emergency info missing",
    "Services unclear",
    "Trust signals missing",
    "Site feels slow or dated"
  ],
  offerHeading: "What You Get",
  offerLead: "A vet website designed for calls and bookings.",
  offerCards: [
    { title: "Phone-first CTAs", text: "Tap-to-call and booking actions made obvious." },
    { title: "Service clarity", text: "Clear pages for key treatments and services." },
    { title: "Emergency messaging", text: "Clear pathway for urgent cases and after-hours." },
    { title: "Trust signals", text: "Reviews and proof placed well." },
    { title: "Local visibility", text: "Structure to support local searches." },
    { title: "Fast load", text: "Quick pages for better conversions." }
  ],
  stepsHeading: "How It Works",
  steps: [
    { title: "Discovery", text: "Confirm services, hours, and booking flow." },
    { title: "Structure", text: "Map pages and CTAs." },
    { title: "Build", text: "Implement and refine." },
    { title: "Launch", text: "Test mobile calls and bookings." }
  ],
  finalHeading: "Want More Vet Bookings?",
  finalLead: "Send your link and we will map the simplest improvements first."
},

{
  key: "websites-for-medical-centres",
  path: "/websites-for-medical-centres",
  title: "Websites for Medical Centres | Bookings and Trust | 2merative Web Design",
  description:
    "Websites for medical centres built for bookings and clarity. Services, doctors, and contact paths made simple and mobile-first.",
  heroHeading: "Websites For Medical Centres Built For Bookings And Trust",
  heroSubheading:
    "Clear services, doctor profiles, and fast booking pathways. Built for mobile-first patients and local visibility.",
  problemHeading: "Patients Need Clear Next Steps",
  problemParagraphs: [
    "Medical centres lose bookings when patients cannot find key details fast.",
    "Hours, locations, services, and booking links must be obvious on mobile.",
    "We build medical centre sites that reduce friction and increase bookings."
  ],
  problemBoxHeading: "Common issues we fix",
  problemBullets: [
    "Booking link hard to find",
    "Hours and location unclear",
    "Services list is confusing",
    "Mobile layout is messy",
    "Doctors not presented well",
    "Site feels outdated"
  ],
  offerHeading: "What You Get",
  offerLead: "A medical centre website designed for bookings and clarity.",
  offerCards: [
    { title: "Booking pathway", text: "Clear booking links and CTAs throughout." },
    { title: "Service clarity", text: "Services structured to match patient intent." },
    { title: "Doctor profiles", text: "Simple, credible profiles that build trust." },
    { title: "Mobile-first", text: "Easy to use on phones." },
    { title: "Local visibility", text: "Structure to support local searches." },
    { title: "Fast load", text: "Quick pages that feel professional." }
  ],
  stepsHeading: "How It Works",
  steps: [
    { title: "Discovery", text: "Confirm services, staff, and booking system." },
    { title: "Structure", text: "Map pages, CTAs, and key info blocks." },
    { title: "Build", text: "Implement and refine." },
    { title: "Launch", text: "Test bookings, calls, and mobile usability." }
  ],
  finalHeading: "Want More Patient Bookings?",
  finalLead: "Send your current site link and we will map the cleanest improvements first."
},

{
  key: "websites-for-day-spas",
  path: "/websites-for-day-spas",
  title: "Websites for Day Spas | Bookings, Trust, Calm Design | 2merative Web Design",
  description:
    "Websites for day spas built for bookings. Calm design, clear services, and an easy enquiry pathway that works on mobile.",
  heroHeading: "Websites For Day Spas Built To Increase Bookings",
  heroSubheading:
    "Calm design, clear services, and a simple booking pathway. Built for mobile-first customers and local search.",
  problemHeading: "If Booking Feels Hard, People Choose Another Spa",
  problemParagraphs: [
    "Spa customers want a smooth experience from the first click.",
    "If your services and pricing feel unclear, or the site feels clunky on mobile, you lose bookings.",
    "We build spa sites that look premium and make booking easy."
  ],
  problemBoxHeading: "Common issues we fix",
  problemBullets: [
    "Services and packages unclear",
    "No clear booking pathway",
    "Mobile pages hard to use",
    "Weak trust signals",
    "Site feels dated or slow",
    "Local visibility is weak"
  ],
  offerHeading: "What You Get",
  offerLead: "A premium feeling website designed for spa bookings.",
  offerCards: [
    { title: "Service clarity", text: "Clear treatments and packages with simple structure." },
    { title: "Booking pathway", text: "Fast calls to action and enquiry flow." },
    { title: "Calm design", text: "Clean layout that feels premium." },
    { title: "Trust signals", text: "Reviews and proof placed well." },
    { title: "Local visibility", text: "Structure to support local searches." },
    { title: "Fast load", text: "Quick pages that convert better." }
  ],
  stepsHeading: "How It Works",
  steps: [
    { title: "Discovery", text: "Confirm services, packages, and booking flow." },
    { title: "Structure", text: "Map pages and CTAs." },
    { title: "Build", text: "Implement and refine." },
    { title: "Launch", text: "Test booking and enquiries on mobile." }
  ],
  finalHeading: "Want More Spa Bookings?",
  finalLead: "Send your current link and we will map the simplest upgrades first."
},

{
  key: "websites-for-barbers",
  path: "/websites-for-barbers",
  title: "Websites for Barbers | More Bookings, More Walk-Ins | 2merative Web Design",
  description:
    "Websites for barbers built for bookings. Clear services, pricing clarity, and fast mobile contact paths.",
  heroHeading: "Websites For Barbers Built To Drive Bookings",
  heroSubheading:
    "Clear services, pricing clarity, and fast mobile booking. Built for local customers who want to book now.",
  problemHeading: "Barber Searches Are Fast And Mobile",
  problemParagraphs: [
    "Customers often choose the shop that looks clean, credible, and easy to book.",
    "If your hours, location, and booking link are not obvious, they move on.",
    "We build barber sites that make booking and contact easy."
  ],
  problemBoxHeading: "Common issues we fix",
  problemBullets: [
    "Booking link not obvious",
    "Hours and location unclear",
    "Pricing confusing",
    "Mobile layout messy",
    "Photos are weak",
    "Site feels dated"
  ],
  offerHeading: "What You Get",
  offerLead: "A barber website designed for bookings and local visibility.",
  offerCards: [
    { title: "Booking pathway", text: "Clear booking CTA on every page." },
    { title: "Hours and location", text: "Easy to find details on mobile." },
    { title: "Service clarity", text: "Simple service list and pricing clarity." },
    { title: "Photo proof", text: "Gallery layout that works on phones." },
    { title: "Local visibility", text: "Structure for suburb and service searches." },
    { title: "Fast load", text: "Quick pages that feel professional." }
  ],
  stepsHeading: "How It Works",
  steps: [
    { title: "Discovery", text: "Confirm services, pricing, and booking platform." },
    { title: "Structure", text: "Map pages and CTAs." },
    { title: "Build", text: "Implement and refine." },
    { title: "Launch", text: "Test bookings and mobile usability." }
  ],
  finalHeading: "Want More Barber Bookings?",
  finalLead: "Send your current site link and we will map the simplest improvements first."
},

{
  key: "websites-for-hair-salons",
  path: "/websites-for-hair-salons",
  title: "Websites for Hair Salons | More Bookings, More Trust | 2merative Web Design",
  description:
    "Websites for hair salons built for bookings. Clear services, strong visual proof, and fast mobile enquiry paths.",
  heroHeading: "Websites For Hair Salons Built To Increase Bookings",
  heroSubheading:
    "Clear services, strong photo proof, and a simple booking path. Built for mobile-first customers and local searches.",
  problemHeading: "If Your Work Is Hard To See, People Do Not Book",
  problemParagraphs: [
    "Hair services are visual. Customers want to see proof quickly.",
    "If booking feels hard or the site is messy on mobile, you lose enquiries.",
    "We build salon sites that showcase work and make booking easy."
  ],
  problemBoxHeading: "Common issues we fix",
  problemBullets: [
    "Gallery is hard to browse",
    "Services and pricing unclear",
    "No clear booking path",
    "Mobile layout messy",
    "No reviews shown",
    "Site feels dated"
  ],
  offerHeading: "What You Get",
  offerLead: "A salon website designed for trust and bookings.",
  offerCards: [
    { title: "Photo-first gallery", text: "A clean showcase that works on mobile." },
    { title: "Service clarity", text: "Simple service list and pricing guidance." },
    { title: "Booking pathway", text: "Clear booking CTAs throughout the site." },
    { title: "Trust signals", text: "Reviews and proof placed well." },
    { title: "Local visibility", text: "Structure to support local searches." },
    { title: "Fast load", text: "Quick pages that convert better." }
  ],
  stepsHeading: "How It Works",
  steps: [
    { title: "Discovery", text: "Confirm services, pricing style, and booking flow." },
    { title: "Structure", text: "Map pages, gallery, and CTAs." },
    { title: "Build", text: "Implement and refine." },
    { title: "Launch", text: "Test booking and mobile usability." }
  ],
  finalHeading: "Want More Salon Bookings?",
  finalLead: "Send your link and we will map the cleanest improvements first."
},

{
  key: "websites-for-beauty-therapists",
  path: "/websites-for-beauty-therapists",
  title: "Websites for Beauty Therapists | Bookings and Trust | 2merative Web Design",
  description:
    "Websites for beauty therapists built for bookings. Clear services, trust signals, and fast enquiry pathways that work on mobile.",
  heroHeading: "Websites For Beauty Therapists Built To Drive Bookings",
  heroSubheading:
    "Clear services, trust signals, and a simple booking path. Built for mobile-first customers and local visibility.",
  problemHeading: "People Book Where It Feels Easy And Trustworthy",
  problemParagraphs: [
    "Beauty clients compare quickly. They want clear services, pricing guidance, and proof.",
    "If your site feels unclear or dated, they move on.",
    "We build beauty therapist sites that showcase work and make booking easy."
  ],
  problemBoxHeading: "Common issues we fix",
  problemBullets: [
    "Services unclear",
    "Pricing confusing",
    "No strong proof or reviews",
    "Booking path hard to find",
    "Mobile layout messy",
    "Site feels slow or outdated"
  ],
  offerHeading: "What You Get",
  offerLead: "A website designed for bookings and trust.",
  offerCards: [
    { title: "Service clarity", text: "Simple service structure and package clarity." },
    { title: "Photo proof", text: "Showcase layout that works on mobile." },
    { title: "Booking pathway", text: "Clear CTAs and enquiry flow." },
    { title: "Trust signals", text: "Reviews and proof placed well." },
    { title: "Local visibility", text: "Structure for local searches." },
    { title: "Fast load", text: "Quick pages that convert better." }
  ],
  stepsHeading: "How It Works",
  steps: [
    { title: "Discovery", text: "Confirm services and booking flow." },
    { title: "Structure", text: "Map pages and CTAs." },
    { title: "Build", text: "Implement and refine." },
    { title: "Launch", text: "Test mobile enquiries and bookings." }
  ],
  finalHeading: "Want More Beauty Bookings?",
  finalLead: "Send your current link and we will map the simplest improvements first."
},

{
  key: "websites-for-personal-trainers",
  path: "/websites-for-personal-trainers",
  title: "Websites for Personal Trainers | Enquiries and Conversions | 2merative Web Design",
  description:
    "Websites for personal trainers built to generate enquiries. Clear offers, proof, and fast mobile contact paths.",
  heroHeading: "Websites For Personal Trainers Built To Win More Enquiries",
  heroSubheading:
    "Clear offers, proof, and simple CTAs. Built for local visibility and mobile-first prospects.",
  problemHeading: "People Need Clarity And Proof Before They Commit",
  problemParagraphs: [
    "Fitness prospects compare quickly and look for proof, outcomes, and a clear offer.",
    "If your site is unclear, you lose enquiries to competitors.",
    "We build PT sites that show proof and guide visitors to contact you."
  ],
  problemBoxHeading: "Common issues we fix",
  problemBullets: [
    "Offer is unclear",
    "No clear enquiry path",
    "Before and after proof missing",
    "Mobile layout messy",
    "Site feels dated",
    "Local visibility weak"
  ],
  offerHeading: "What You Get",
  offerLead: "A personal trainer website designed for enquiries.",
  offerCards: [
    { title: "Offer clarity", text: "Clear packages and next step guidance." },
    { title: "Proof blocks", text: "Testimonials and results positioned well." },
    { title: "Mobile CTAs", text: "Fast call and message actions." },
    { title: "Local visibility", text: "Structure for local searches." },
    { title: "Fast load", text: "Quick pages that convert better." },
    { title: "Simple updates", text: "Clean content layout for easy changes." }
  ],
  stepsHeading: "How It Works",
  steps: [
    { title: "Discovery", text: "Confirm your offer and audience." },
    { title: "Structure", text: "Map pages and CTAs." },
    { title: "Build", text: "Implement and refine." },
    { title: "Launch", text: "Test mobile enquiries and contact flow." }
  ],
  finalHeading: "Want More PT Enquiries?",
  finalLead: "Send your current link and we will map the simplest upgrades first."
},

{
  key: "websites-for-gyms",
  path: "/websites-for-gyms",
  title: "Websites for Gyms | Membership Enquiries and Signups | 2merative Web Design",
  description:
    "Websites for gyms built to increase membership enquiries. Clear offers, class schedules, and a simple signup path.",
  heroHeading: "Websites For Gyms Built To Increase Membership Enquiries",
  heroSubheading:
    "Clear offers, class info, and an easy signup path. Built for local searches and mobile-first prospects.",
  problemHeading: "If Your Offer Is Unclear, People Do Not Join",
  problemParagraphs: [
    "Gym prospects want clear pricing, class info, and an easy way to start.",
    "If your site is cluttered or confusing on mobile, you lose signups.",
    "We build gym sites that make joining simple."
  ],
  problemBoxHeading: "Common issues we fix",
  problemBullets: [
    "Membership offers unclear",
    "Class info hard to find",
    "Signup path not obvious",
    "Mobile layout messy",
    "No trust or proof shown",
    "Local visibility weak"
  ],
  offerHeading: "What You Get",
  offerLead: "A gym website designed for membership enquiries.",
  offerCards: [
    { title: "Offer clarity", text: "Clear membership options and next steps." },
    { title: "Schedule blocks", text: "Simple class and timetable layout." },
    { title: "Signup CTA", text: "Clear calls to action that convert." },
    { title: "Proof and trust", text: "Testimonials and results positioned well." },
    { title: "Local visibility", text: "Structure for local intent searches." },
    { title: "Fast load", text: "Quick pages for better conversions." }
  ],
  stepsHeading: "How It Works",
  steps: [
    { title: "Discovery", text: "Confirm offers, classes, and signup flow." },
    { title: "Structure", text: "Map key pages and CTAs." },
    { title: "Build", text: "Implement and refine." },
    { title: "Launch", text: "Test signups and mobile usability." }
  ],
  finalHeading: "Want More Gym Membership Enquiries?",
  finalLead: "Send your current site link and we will map the cleanest improvements first."
},

{
  key: "websites-for-yoga-studios",
  path: "/websites-for-yoga-studios",
  title: "Websites for Yoga Studios | Bookings and Memberships | 2merative Web Design",
  description:
    "Websites for yoga studios built for bookings. Clear class info, trust signals, and an easy path to join or book.",
  heroHeading: "Websites For Yoga Studios Built To Increase Bookings",
  heroSubheading:
    "Clear class info, calm design, and easy booking pathways. Built for local visibility and mobile-first visitors.",
  problemHeading: "People Want A Calm, Clear Path To Book",
  problemParagraphs: [
    "Yoga clients want to see class types, schedule, and how to book without friction.",
    "If your site is confusing on mobile, you lose bookings.",
    "We build yoga studio sites that feel calm and convert."
  ],
  problemBoxHeading: "Common issues we fix",
  problemBullets: [
    "Class info unclear",
    "Timetable hard to find",
    "No clear booking path",
    "Mobile layout hard to use",
    "No trust signals",
    "Site feels dated"
  ],
  offerHeading: "What You Get",
  offerLead: "A yoga studio website designed for bookings and memberships.",
  offerCards: [
    { title: "Class clarity", text: "Clear class types and who they suit." },
    { title: "Timetable blocks", text: "Simple schedule presentation." },
    { title: "Booking pathway", text: "Clear booking CTAs throughout." },
    { title: "Calm design", text: "Clean layout that feels premium." },
    { title: "Local visibility", text: "Structure for local searches." },
    { title: "Fast load", text: "Quick pages that convert better." }
  ],
  stepsHeading: "How It Works",
  steps: [
    { title: "Discovery", text: "Confirm class types and booking system." },
    { title: "Structure", text: "Map timetable and CTAs." },
    { title: "Build", text: "Implement and refine." },
    { title: "Launch", text: "Test mobile bookings." }
  ],
  finalHeading: "Want More Yoga Bookings?",
  finalLead: "Send your link and we will map the cleanest improvements first."
},

{
  key: "websites-for-coffee-shops",
  path: "/websites-for-coffee-shops",
  title: "Websites for Coffee Shops | More Customers, More Orders | 2merative Web Design",
  description:
    "Websites for coffee shops built to drive foot traffic and orders. Menus, hours, location, and calls to action made simple on mobile.",
  heroHeading: "Websites For Coffee Shops Built To Bring In More Customers",
  heroSubheading:
    "Menus, hours, location, and fast mobile contact paths. Built for local discovery and real customers.",
  problemHeading: "Cafe Searches Are Fast And Mobile",
  problemParagraphs: [
    "People search when hungry. They want hours, menu, location, and how to order.",
    "If that information is hard to find, they choose the next cafe.",
    "We build cafe sites that make the essentials obvious."
  ],
  problemBoxHeading: "Common issues we fix",
  problemBullets: [
    "Menu hard to read on mobile",
    "Hours and location unclear",
    "No clear order or booking path",
    "Slow pages on mobile data",
    "Photos weak or missing",
    "Local visibility is poor"
  ],
  offerHeading: "What You Get",
  offerLead: "A cafe website designed for local customers and quick decisions.",
  offerCards: [
    { title: "Menu clarity", text: "Mobile-friendly menu layout." },
    { title: "Hours and location", text: "Easy to find and tap for directions." },
    { title: "Order and booking CTAs", text: "Clear next steps for customers." },
    { title: "Photo proof", text: "Showcase that sells your food." },
    { title: "Local visibility", text: "Structure for suburb searches." },
    { title: "Fast load", text: "Quick pages that keep customers." }
  ],
  stepsHeading: "How It Works",
  steps: [
    { title: "Discovery", text: "Confirm menu, offers, and CTAs." },
    { title: "Structure", text: "Map key sections and content." },
    { title: "Build", text: "Implement and refine." },
    { title: "Launch", text: "Test mobile menu and contact paths." }
  ],
  finalHeading: "Want More Cafe Customers?",
  finalLead: "Send your link and we will map the simplest upgrades first."
},

{
  key: "websites-for-restaurants",
  path: "/websites-for-restaurants",
  title: "Websites for Restaurants | Bookings and Orders | 2merative Web Design",
  description:
    "Websites for restaurants built for bookings. Menus, hours, booking links, and mobile-first UX that converts.",
  heroHeading: "Websites For Restaurants Built To Increase Bookings",
  heroSubheading:
    "Menus, booking links, and clear info that works on mobile. Built for local search and real customers.",
  problemHeading: "If The Menu Or Booking Link Is Hard To Find, You Lose Covers",
  problemParagraphs: [
    "Restaurant customers decide quickly. They want the menu, photos, hours, and how to book.",
    "If the site is slow or confusing on mobile, they choose another venue.",
    "We build restaurant sites that make booking simple."
  ],
  problemBoxHeading: "Common issues we fix",
  problemBullets: [
    "Menu not mobile friendly",
    "Booking link hard to find",
    "Hours and location unclear",
    "Slow pages",
    "Photos weak",
    "Local visibility poor"
  ],
  offerHeading: "What You Get",
  offerLead: "A restaurant website designed for bookings and confidence.",
  offerCards: [
    { title: "Menu clarity", text: "Mobile-friendly menu layout." },
    { title: "Booking pathway", text: "Clear booking CTA throughout." },
    { title: "Hours and location", text: "Easy to find and tap for directions." },
    { title: "Photo proof", text: "Showcase that sells your venue." },
    { title: "Local visibility", text: "Structure for suburb searches." },
    { title: "Fast load", text: "Quick pages that convert better." }
  ],
  stepsHeading: "How It Works",
  steps: [
    { title: "Discovery", text: "Confirm menu, booking system, and CTAs." },
    { title: "Structure", text: "Map key pages and content." },
    { title: "Build", text: "Implement and refine." },
    { title: "Launch", text: "Test mobile menu and booking." }
  ],
  finalHeading: "Want More Restaurant Bookings?",
  finalLead: "Send your current link and we will map the cleanest improvements first."
},

{
  key: "websites-for-takeaway-shops",
  path: "/websites-for-takeaway-shops",
  title: "Websites for Takeaway Shops | Orders and Local Visibility | 2merative Web Design",
  description:
    "Websites for takeaway shops built to drive orders. Menus, ordering links, and mobile-first layout that converts.",
  heroHeading: "Websites For Takeaway Shops Built To Drive More Orders",
  heroSubheading:
    "Clear menus, ordering links, and fast mobile UX. Built for local search and customers ordering now.",
  problemHeading: "Takeaway Customers Decide Fast",
  problemParagraphs: [
    "Most customers search on their phone and want to order quickly.",
    "If the menu is hard to read or the ordering link is missing, they choose another shop.",
    "We build takeaway sites that make ordering easy."
  ],
  problemBoxHeading: "Common issues we fix",
  problemBullets: [
    "Menu not readable on mobile",
    "Ordering link not obvious",
    "Hours and location unclear",
    "Slow pages on mobile data",
    "Photos weak",
    "Local discovery is poor"
  ],
  offerHeading: "What You Get",
  offerLead: "A takeaway website designed for quick orders.",
  offerCards: [
    { title: "Mobile menu", text: "Fast, readable menu layout." },
    { title: "Order CTAs", text: "Clear links to order online or call." },
    { title: "Hours and location", text: "Easy to find with tap directions." },
    { title: "Photo proof", text: "Showcase that sells your food." },
    { title: "Local visibility", text: "Structure for suburb searches." },
    { title: "Fast load", text: "Quick pages that convert better." }
  ],
  stepsHeading: "How It Works",
  steps: [
    { title: "Discovery", text: "Confirm menu and ordering path." },
    { title: "Structure", text: "Map key sections and CTAs." },
    { title: "Build", text: "Implement and refine." },
    { title: "Launch", text: "Test mobile menu and ordering." }
  ],
  finalHeading: "Want More Takeaway Orders?",
  finalLead: "Send your link and we will map the simplest improvements first."
},

{
  key: "websites-for-automatic-gates",
  path: "/websites-for-automatic-gates",
  title: "Websites for Automatic Gate Installers | Quotes and Calls | 2merative Web Design",
  description:
    "Websites for automatic gate installers built for quote requests. Clear services, proof, and fast mobile contact paths.",
  heroHeading: "Websites For Gate Installers Built To Win Better Quote Requests",
  heroSubheading:
    "Clear services, strong proof, and a simple quote pathway. Built for local discovery and mobile-first customers.",
  problemHeading: "High-Value Jobs Need Proof And Clarity",
  problemParagraphs: [
    "Customers want confidence before they request a quote for higher-value work.",
    "If your proof is weak or your services are unclear, they hesitate.",
    "We build installer websites that show proof and guide enquiries."
  ],
  problemBoxHeading: "Common issues we fix",
  problemBullets: [
    "Services unclear",
    "No clear quote request pathway",
    "Project proof missing",
    "Mobile layout messy",
    "Service areas not listed",
    "Site feels dated"
  ],
  offerHeading: "What You Get",
  offerLead: "A website built to win quote requests for high-value installs.",
  offerCards: [
    { title: "Service clarity", text: "Clear pages for installs, repairs, and automation." },
    { title: "Project proof", text: "Gallery and case study layout that sells." },
    { title: "Quote pathway", text: "Simple enquiry flow that captures essentials." },
    { title: "Service areas", text: "Suburbs and regions listed clearly." },
    { title: "Trust signals", text: "Reviews and proof placed well." },
    { title: "Fast load", text: "Quick pages that feel premium." }
  ],
  stepsHeading: "How It Works",
  steps: [
    { title: "Discovery", text: "Confirm services, areas, and ideal jobs." },
    { title: "Structure", text: "Map pages and proof blocks." },
    { title: "Build", text: "Implement and refine." },
    { title: "Launch", text: "Test mobile enquiries and calls." }
  ],
  finalHeading: "Want Better Quote Requests?",
  finalLead: "Send your link and we will map the simplest upgrade path."
},

{
  key: "websites-for-pest-control",
  path: "/websites-for-pest-control",
  title: "Websites for Pest Control | More Calls, More Jobs | 2merative Web Design",
  description:
    "Websites for pest control built to generate calls. Clear services, service areas, and fast mobile contact paths.",
  heroHeading: "Websites For Pest Control Built To Drive Calls Fast",
  heroSubheading:
    "Clear services, service areas, and fast mobile contact. Built for local customers searching right now.",
  problemHeading: "Pest Searches Are Urgent",
  problemParagraphs: [
    "Most pest control customers want help now. They choose the business they can contact quickly.",
    "If your number is not obvious or your site is slow, you lose jobs.",
    "We build pest control sites that make calling and booking easy."
  ],
  problemBoxHeading: "Common issues we fix",
  problemBullets: [
    "Phone number not obvious",
    "Services unclear",
    "Service areas missing",
    "No trust signals or proof",
    "Slow pages",
    "No clear booking path"
  ],
  offerHeading: "What You Get",
  offerLead: "A pest control website designed for calls and jobs.",
  offerCards: [
    { title: "Phone-first CTAs", text: "Tap-to-call is obvious and easy." },
    { title: "Service clarity", text: "Clear services customers recognise." },
    { title: "Areas covered", text: "Suburbs and regions listed clearly." },
    { title: "Trust signals", text: "Reviews and proof placed well." },
    { title: "Fast load", text: "Quick pages that convert better." },
    { title: "Simple enquiry form", text: "Capture essentials without friction." }
  ],
  stepsHeading: "How It Works",
  steps: [
    { title: "Quick chat", text: "Confirm services, areas, and priorities." },
    { title: "Structure", text: "Map pages and CTAs." },
    { title: "Build", text: "Implement and refine." },
    { title: "Launch", text: "Test mobile calls and enquiries." }
  ],
  finalHeading: "Want More Pest Control Jobs?",
  finalLead: "Send your current site link and we will map the simplest improvements first."
},

{
  key: "websites-for-appliance-repairs",
  path: "/websites-for-appliance-repairs",
  title: "Websites for Appliance Repairers | More Calls, More Jobs | 2merative Web Design",
  description:
    "Websites for appliance repairers built for calls and bookings. Clear services, service areas, and fast mobile contact paths.",
  heroHeading: "Websites For Appliance Repairers Built To Win More Calls",
  heroSubheading:
    "Clear services, service areas, and fast mobile contact. Built for local customers searching right now.",
  problemHeading: "Repair Customers Want A Fast Answer",
  problemParagraphs: [
    "Most repair searches are urgent. People want to know you service their appliance and how to book.",
    "If your site is unclear, they call someone else.",
    "We build repair websites that make calling and booking easy."
  ],
  problemBoxHeading: "Common issues we fix",
  problemBullets: [
    "Services unclear",
    "Appliances serviced not listed",
    "No clear booking path",
    "Mobile calling is not obvious",
    "Service areas missing",
    "Site feels outdated"
  ],
  offerHeading: "What You Get",
  offerLead: "A repair website designed for calls and bookings.",
  offerCards: [
    { title: "Service clarity", text: "List appliances and services clearly." },
    { title: "Phone-first CTA", text: "Tap-to-call made obvious." },
    { title: "Booking pathway", text: "Simple enquiry form that captures essentials." },
    { title: "Areas covered", text: "Suburbs and regions listed clearly." },
    { title: "Trust signals", text: "Reviews and proof placed well." },
    { title: "Fast load", text: "Quick pages that convert better." }
  ],
  stepsHeading: "How It Works",
  steps: [
    { title: "Discovery", text: "Confirm services, appliances, and service areas." },
    { title: "Structure", text: "Map pages and CTAs." },
    { title: "Build", text: "Implement and refine." },
    { title: "Launch", text: "Test mobile calls and enquiries." }
  ],
  finalHeading: "Want More Repair Bookings?",
  finalLead: "Send your current link and we will map the simplest upgrade path."
},

{
  key: "websites-for-it-support",
  path: "/websites-for-it-support",
  title: "Websites for IT Support | More Leads, More Trust | 2merative Web Design",
  description:
    "Websites for IT support businesses built for leads. Clear services, trust signals, and simple enquiry paths for businesses.",
  heroHeading: "Websites For IT Support Built To Generate Better Leads",
  heroSubheading:
    "Clear services, trust signals, and simple enquiry paths. Built for local discovery and B2B credibility.",
  problemHeading: "B2B Prospects Need Confidence",
  problemParagraphs: [
    "Businesses want a provider that looks established and easy to work with.",
    "If your services are unclear or your site looks dated, you lose leads.",
    "We build IT support sites that feel credible and convert enquiries."
  ],
  problemBoxHeading: "Common issues we fix",
  problemBullets: [
    "Services unclear",
    "No clear enquiry path",
    "Proof and case studies missing",
    "Site feels dated",
    "Mobile experience weak",
    "Local discovery is poor"
  ],
  offerHeading: "What You Get",
  offerLead: "A website designed to convert business enquiries.",
  offerCards: [
    { title: "Service clarity", text: "Clear pages for managed IT, support, and projects." },
    { title: "Trust signals", text: "Proof, reviews, and credibility blocks." },
    { title: "Lead pathway", text: "Simple enquiry flow with the right fields." },
    { title: "Local visibility", text: "Structure for local B2B discovery." },
    { title: "Fast load", text: "Quick pages that feel professional." },
    { title: "Simple updates", text: "Clean layout that stays maintainable." }
  ],
  stepsHeading: "How It Works",
  steps: [
    { title: "Discovery", text: "Confirm services and ideal clients." },
    { title: "Structure", text: "Map pages and enquiry pathway." },
    { title: "Build", text: "Implement and refine." },
    { title: "Launch", text: "Test enquiries and mobile usability." }
  ],
  finalHeading: "Want Better IT Support Leads?",
  finalLead: "Send your current link and we will map the simplest improvements first."
},

{
  key: "websites-for-marketing-agencies",
  path: "/websites-for-marketing-agencies",
  title: "Websites for Marketing Agencies | Credibility, Leads, Proof | 2merative Web Design",
  description:
    "Websites for marketing agencies built to win better clients. Clear positioning, proof, and a simple enquiry pathway.",
  heroHeading: "Websites For Marketing Agencies Built To Win Better Clients",
  heroSubheading:
    "Clear positioning, strong proof, and simple enquiry pathways. Built to support credibility and conversion.",
  problemHeading: "Agencies Need Positioning And Proof",
  problemParagraphs: [
    "Prospects decide quickly. Your positioning must be clear and backed by proof.",
    "If your site feels generic, you attract the wrong leads.",
    "We build agency sites that clarify what you do and who you do it for."
  ],
  problemBoxHeading: "Common issues we fix",
  problemBullets: [
    "Positioning unclear",
    "Proof buried",
    "No clear next step",
    "Site feels generic",
    "Mobile layout weak",
    "Local discovery low"
  ],
  offerHeading: "What You Get",
  offerLead: "A website designed for positioning, credibility, and leads.",
  offerCards: [
    { title: "Positioning clarity", text: "Clear offer and ideal client alignment." },
    { title: "Proof blocks", text: "Case studies and results highlighted well." },
    { title: "Lead pathway", text: "Simple enquiry flow that filters better." },
    { title: "Fast load", text: "Performance that feels premium." },
    { title: "Mobile-first", text: "Great experience on phones." },
    { title: "Simple updates", text: "Clean structure for ongoing changes." }
  ],
  stepsHeading: "How It Works",
  steps: [
    { title: "Discovery", text: "Confirm positioning and ideal clients." },
    { title: "Structure", text: "Map proof blocks and CTAs." },
    { title: "Build", text: "Implement and refine." },
    { title: "Launch", text: "Test enquiry flow and mobile usability." }
  ],
  finalHeading: "Want Better Clients, Not More Noise?",
  finalLead: "Send your current site link and we will map the simplest improvements first."
},

{
  key: "my-website-is-not-getting-enquiries",
  path: "/my-website-is-not-getting-enquiries",
  title: "My Website Is Not Getting Enquiries | Fix The Conversion Path | 2merative",
  description:
    "If your website is not getting enquiries, something is broken in the path. Find the friction points and fix what matters first.",
  heroHeading: "If Your Website Is Not Getting Enquiries, The Path Is Broken",
  heroSubheading:
    "We find what is stopping visitors from calling, messaging, or booking. Then we give you a clear fix plan you can act on.",
  problemHeading: "Most Websites Do Not Have A Traffic Problem",
  problemParagraphs: [
    "Often the issue is clarity and friction. People arrive, feel unsure, and leave.",
    "Small problems like weak headings, confusing pages, slow load times, or hidden contact details can kill conversions.",
    "This is a focused review to identify what matters and what to fix first."
  ],
  problemBoxHeading: "Common reasons enquiries drop",
  problemBullets: [
    "No clear call to action",
    "Services unclear",
    "Trust signals missing",
    "Contact details hard to find on mobile",
    "Site is slow",
    "Pages feel confusing or cluttered"
  ],
  offerHeading: "What We Fix First",
  offerLead: "A practical set of improvements focused on enquiries, not fluff.",
  offerCards: [
    { title: "Clarity audit", text: "Headings and structure that match how customers decide." },
    { title: "Trust signals", text: "Proof blocks placed where people look." },
    { title: "Conversion pathway", text: "Make calls, messages, and bookings obvious." },
    { title: "Mobile friction", text: "Fix tap targets, spacing, and readability." },
    { title: "Speed wins", text: "Reduce load time where it matters." },
    { title: "Priority plan", text: "A simple ranked list of fixes." }
  ],
  stepsHeading: "How It Works",
  steps: [
    { title: "Quick chat", text: "We confirm goals and what an enquiry looks like." },
    { title: "Review", text: "We identify the main friction points." },
    { title: "Fix plan", text: "You get a clear ranked list of changes." },
    { title: "Implement", text: "Optional support if you want us to do the work." }
  ],
  finalHeading: "Want A Clear Fix Plan?",
  finalLead: "Send your site link and we will tell you what is blocking enquiries."
},

{
  key: "website-not-showing-on-google",
  path: "/website-not-showing-on-google",
  title: "Website Not Showing On Google | Fix Visibility Issues | 2merative",
  description:
    "If your website is not showing on Google, there are usually a few common causes. Get a clear fix plan to improve visibility.",
  heroHeading: "If Your Website Is Not Showing On Google, Something Is Missing",
  heroSubheading:
    "We find the gaps that stop indexing and visibility, then give you a simple plan to fix them.",
  problemHeading: "Visibility Problems Often Come From A Few Common Gaps",
  problemParagraphs: [
    "Sometimes Google cannot properly crawl your site. Sometimes the pages do not match what people search for.",
    "You do not need more random content. You need the basics done right.",
    "This review identifies what is missing and what to fix first."
  ],
  problemBoxHeading: "Common visibility issues",
  problemBullets: [
    "No indexing signals or sitemap issues",
    "Thin or unclear service pages",
    "No local signals",
    "Weak site structure",
    "Poor internal linking",
    "Google Business Profile mismatch"
  ],
  offerHeading: "What We Check",
  offerLead: "Practical checks that affect real search visibility.",
  offerCards: [
    { title: "Indexing basics", text: "Sitemap, robots, and crawl visibility." },
    { title: "Page structure", text: "Headings and content layout for intent." },
    { title: "Local signals", text: "Service areas and local relevance." },
    { title: "Technical health", text: "Common blockers that break visibility." },
    { title: "GBP alignment", text: "Match your website and Google profile." },
    { title: "Fix plan", text: "Clear priorities, not a long report." }
  ],
  stepsHeading: "How It Works",
  steps: [
    { title: "Quick check", text: "Confirm your services and target area." },
    { title: "Review", text: "Identify visibility gaps and blockers." },
    { title: "Plan", text: "Provide a ranked fix list." },
    { title: "Support", text: "Optional help to implement fixes." }
  ],
  finalHeading: "Want To Know Why Google Is Ignoring Your Site?",
  finalLead: "Send your link and we will map the simplest visibility fixes first."
},

{
  key: "local-seo-for-small-business",
  path: "/local-seo-for-small-business",
  title: "Local SEO for Small Business | Get Found Locally | 2merative Web Design",
  description:
    "Local SEO for small business focused on real enquiries. Improve local visibility with clear service pages, local signals, and trust.",
  heroHeading: "Local SEO For Small Business That Drives Real Enquiries",
  heroSubheading:
    "Improve local visibility with the basics done properly. Clear services, local signals, and trust that converts.",
  problemHeading: "Local Search Rewards Clarity And Consistency",
  problemParagraphs: [
    "Most small businesses do not need complicated SEO. They need consistent basics done well.",
    "Clear services, clear service areas, and consistent details across your site and Google profile matters.",
    "This service focuses on practical improvements that drive enquiries."
  ],
  problemBoxHeading: "Common local SEO gaps",
  problemBullets: [
    "No service area clarity",
    "Services too vague",
    "Google profile not aligned",
    "Weak trust signals",
    "Pages not structured for intent",
    "No clear internal linking"
  ],
  offerHeading: "What You Get",
  offerLead: "Practical local SEO improvements focused on enquiries.",
  offerCards: [
    { title: "Service structure", text: "Clear pages for what you actually sell." },
    { title: "Local signals", text: "Service areas and location relevance." },
    { title: "Trust signals", text: "Reviews and proof where it matters." },
    { title: "GBP alignment", text: "Make website and profile match." },
    { title: "Content clarity", text: "Headings and layout for real searches." },
    { title: "Fix plan", text: "A simple priority list of actions." }
  ],
  stepsHeading: "How It Works",
  steps: [
    { title: "Discovery", text: "Confirm services, areas, and priorities." },
    { title: "Review", text: "Identify visibility and trust gaps." },
    { title: "Implement", text: "Apply the key improvements." },
    { title: "Support", text: "Optional ongoing help if you want it maintained." }
  ],
  finalHeading: "Want More Local Enquiries?",
  finalLead: "Send your link and we will map the simplest local SEO improvements first."
},

{
  key: "websites-for-sports-clubs",
  path: "/websites-for-sports-clubs",
  title: "Websites for Sports Clubs | Sponsors, Members, Registrations | 2merative",
  description:
    "Websites for sports clubs built to support registrations, sponsors, and communication. Clear pages, fast updates, and mobile-first layout.",
  heroHeading: "Websites For Sports Clubs Built For Members And Sponsors",
  heroSubheading:
    "Fixtures, events, registrations, sponsors, and updates made simple. Built for mobile-first members and volunteers.",
  problemHeading: "Clubs Need Clarity, Not Complexity",
  problemParagraphs: [
    "Most clubs are run by volunteers. The website must be simple to manage and easy for members to use.",
    "When the site is unclear, the club gets repeated questions and missed registrations.",
    "We build club sites that make key information easy to find."
  ],
  problemBoxHeading: "Common club website problems",
  problemBullets: [
    "Members cannot find key info",
    "Registrations are confusing",
    "Sponsors not presented well",
    "Events and fixtures hard to find",
    "Mobile layout messy",
    "Updates are hard to publish"
  ],
  offerHeading: "What You Get",
  offerLead: "A club website designed for members, volunteers, and sponsors.",
  offerCards: [
    { title: "Key info hubs", text: "Fixtures, events, regos, and contacts easy to find." },
    { title: "Sponsor value", text: "Sponsor tiles and pages that look professional." },
    { title: "Mobile-first", text: "Designed for members on phones." },
    { title: "Fast updates", text: "Simple structure for quick publishing." },
    { title: "Trust signals", text: "Clear club identity and credibility." },
    { title: "Local visibility", text: "Support local searches and discovery." }
  ],
  stepsHeading: "How It Works",
  steps: [
    { title: "Discovery", text: "Confirm needs, volunteers, and key workflows." },
    { title: "Structure", text: "Map the pages members actually use." },
    { title: "Build", text: "Implement and refine quickly." },
    { title: "Launch", text: "Test on mobile and support rollout." }
  ],
  finalHeading: "Want A Club Website That Volunteers Can Actually Run?",
  finalLead: "Send your current site link and we will map the simplest improvements first."
},

  {
    key: "people-visit-but-dont-contact",
    path: "/people-visit-but-dont-contact",
    title: "People Visit But Don’t Contact | Fix Your Website Conversion Path | 2merative",
    description:
      "If people visit your site but do not contact you, the conversion path has friction. We find it and give you a clear fix plan.",
    heroHeading: "People Visit Your Website But Do Not Contact You",
    heroSubheading:
      "That usually means friction, confusion, or missing trust. We identify the blockers and give you a simple fix plan.",
    problemHeading: "Traffic Is Not Enough If The Next Step Is Unclear",
    problemParagraphs: [
      "Most visitors decide in seconds if they understand what you do and how to take the next step.",
      "Small issues like vague headings, hidden phone numbers, or weak proof can quietly kill enquiries.",
      "This review focuses on conversion, not vanity metrics.",
    ],
    problemBoxHeading: "Common conversion blockers",
    problemBullets: [
      "No clear call to action",
      "Services unclear or too broad",
      "Contact details hard to find",
      "Weak trust signals",
      "Mobile layout friction",
      "Pages feel cluttered",
    ],
    offerHeading: "What You Get",
    offerLead: "A focused conversion fix plan that improves enquiries.",
    offerCards: [
      { title: "Clarity pass", text: "Fix headings and page structure so visitors understand fast." },
      { title: "Trust upgrades", text: "Add proof blocks where people look before they enquire." },
      { title: "CTA pathway", text: "Make the next step obvious on every page." },
      { title: "Mobile friction fixes", text: "Improve tap targets, spacing, and readability." },
      { title: "Form and contact checks", text: "Ensure contact methods are working properly." },
      { title: "Priority list", text: "Ranked actions that make the biggest impact first." },
    ],
    stepsHeading: "How It Works",
    steps: [
      { title: "Quick chat", text: "Confirm what a good enquiry looks like." },
      { title: "Audit", text: "Review key pages and conversion path." },
      { title: "Plan", text: "Deliver a clear ranked fix list." },
      { title: "Optional build", text: "We can implement fixes if you want." },
    ],
    finalHeading: "Want More Enquiries From The Same Traffic?",
    finalLead: "Send your site link and we will map the simplest conversion fixes first.",
  },

  {
    key: "too-many-tyre-kickers",
    path: "/too-many-tyre-kickers",
    title: "Too Many Tyre-Kickers | Attract Better Leads | 2merative",
    description:
      "If you get lots of low-quality enquiries, your website may not be filtering properly. Fix messaging, proof, and the enquiry path.",
    heroHeading: "Too Many Tyre-Kickers And Low-Quality Enquiries",
    heroSubheading:
      "Your website should filter. We tighten the message and enquiry flow so you get better leads.",
    problemHeading: "Your Site Might Be Attracting The Wrong People",
    problemParagraphs: [
      "Low-quality enquiries usually come from unclear positioning and vague offers.",
      "If pricing expectations and service scope are not clear, you get time-wasters.",
      "We help you tighten the message so the right people contact you.",
    ],
    problemBoxHeading: "Common causes",
    problemBullets: [
      "Offer is vague",
      "No qualification steps",
      "Services too broad",
      "Proof is missing",
      "No clear ideal customer",
      "Enquiry form asks the wrong questions",
    ],
    offerHeading: "What You Get",
    offerLead: "A website that attracts better-fit enquiries.",
    offerCards: [
      { title: "Positioning clarity", text: "Make your offer and ideal customer clear." },
      { title: "Scope boundaries", text: "Reduce mismatch with clear service scope." },
      { title: "Proof placement", text: "Add trust signals that justify your value." },
      { title: "Better enquiry form", text: "Ask the right questions to filter." },
      { title: "CTA alignment", text: "Match CTAs to the jobs you want." },
      { title: "Priority plan", text: "Clear ranked actions to improve lead quality." },
    ],
    stepsHeading: "How It Works",
    steps: [
      { title: "Discovery", text: "Define ideal jobs and problem leads." },
      { title: "Audit", text: "Review pages and enquiry flow." },
      { title: "Rewrite", text: "Tighten key sections and CTAs." },
      { title: "Deploy", text: "Implement updates and test." },
    ],
    finalHeading: "Want Fewer Time-Wasters And Better Leads?",
    finalLead: "Send your site link and we will map the simplest filtering improvements.",
  },

  {
    key: "contact-form-not-working",
    path: "/contact-form-not-working",
    title: "Contact Form Not Working | Fix Lost Leads | 2merative",
    description:
      "If your contact form is not working, you may be losing leads. We find the issue, fix it, and test the full enquiry path.",
    heroHeading: "If Your Contact Form Is Not Working, You Are Losing Leads",
    heroSubheading:
      "We check the full enquiry path, fix the issue, and confirm messages land where they should.",
    problemHeading: "Forms Break More Often Than People Think",
    problemParagraphs: [
      "Email routing, spam filtering, DNS changes, plugin issues, and hosting settings can break forms.",
      "Often the form submits but the message never arrives.",
      "We fix the root cause and test end to end.",
    ],
    problemBoxHeading: "Common form failures",
    problemBullets: [
      "Messages go to spam",
      "Wrong destination email",
      "SMTP not configured",
      "Captcha blocking legit users",
      "JavaScript errors",
      "DNS or domain changes broke email",
    ],
    offerHeading: "What You Get",
    offerLead: "A working enquiry path that you can trust.",
    offerCards: [
      { title: "Form diagnostics", text: "Find what is failing and why." },
      { title: "Email delivery fix", text: "Improve delivery and reduce spam issues." },
      { title: "Fallback options", text: "Add phone and alternate contact pathways." },
      { title: "Spam protection", text: "Reduce junk without blocking real leads." },
      { title: "Testing", text: "Verify the full path with real tests." },
      { title: "Simple report", text: "Clear summary of what we changed." },
    ],
    stepsHeading: "How It Works",
    steps: [
      { title: "Check", text: "Reproduce and identify the failure point." },
      { title: "Fix", text: "Apply the correct delivery or form changes." },
      { title: "Test", text: "Run multiple device tests and confirm delivery." },
      { title: "Harden", text: "Add sensible spam protection." },
    ],
    finalHeading: "Want To Stop Losing Leads?",
    finalLead: "Send your site link and we will find the form break and fix it.",
  },

  {
    key: "website-not-getting-enquiries",
    path: "/website-not-getting-enquiries",
    title: "Website Not Getting Enquiries | Fix What Is Blocking Leads | 2merative",
    description:
      "If your website is not getting enquiries, the message, trust, or next step is usually broken. Get a clear fix plan.",
    heroHeading: "Your Website Is Not Getting Enquiries",
    heroSubheading:
      "We find what is blocking calls and messages, explain it clearly, and give you a simple fix plan.",
    problemHeading: "Most Websites Fail For A Few Predictable Reasons",
    problemParagraphs: [
      "Visitors decide fast. If they do not understand, trust, or know what to do next, they leave.",
      "You do not need more random changes. You need the right fixes first.",
      "We focus on conversion and clarity.",
    ],
    problemBoxHeading: "Common reasons enquiries are low",
    problemBullets: [
      "Headline and services unclear",
      "No proof and trust signals",
      "Contact details not obvious",
      "Mobile friction",
      "Pages too slow",
      "Wrong traffic from unclear messaging",
    ],
    offerHeading: "What You Get",
    offerLead: "A clear ranked plan to increase enquiries.",
    offerCards: [
      { title: "Clarity improvements", text: "Fix messaging so visitors understand fast." },
      { title: "Trust upgrades", text: "Add proof blocks where it matters." },
      { title: "CTA pathway", text: "Make the next step obvious on every page." },
      { title: "Mobile fixes", text: "Improve usability on phones." },
      { title: "Speed wins", text: "Remove common speed bottlenecks." },
      { title: "Priority plan", text: "Ranked actions and next steps." },
    ],
    stepsHeading: "How It Works",
    steps: [
      { title: "Quick chat", text: "Confirm your services and ideal enquiries." },
      { title: "Audit", text: "Review key pages and conversion path." },
      { title: "Plan", text: "Deliver ranked improvements." },
      { title: "Optional build", text: "We can implement fixes if you want." },
    ],
    finalHeading: "Want More Enquiries Without Guessing?",
    finalLead: "Send your site link and we will map the simplest fixes first.",
  },

  {
    key: "website-not-showing-on-google",
    path: "/website-not-showing-on-google",
    title: "Website Not Showing On Google | Fix Visibility Issues | 2merative",
    description:
      "If your website is not showing on Google, you may have indexing, structure, or trust gaps. Get a clear fix plan.",
    heroHeading: "Your Website Is Not Showing On Google",
    heroSubheading:
      "We identify the likely cause and give you a practical plan to get pages indexed and visible.",
    problemHeading: "Visibility Fails When The Basics Are Missing",
    problemParagraphs: [
      "Many sites are not indexed properly or have weak page structure for search intent.",
      "Sometimes the issue is technical. Sometimes it is content clarity.",
      "We check the right things and prioritise fixes.",
    ],
    problemBoxHeading: "Common causes",
    problemBullets: [
      "Pages not indexed",
      "No sitemap or Search Console setup",
      "Weak page structure and headings",
      "Duplicate content issues",
      "Wrong canonical settings",
      "Thin or unclear service pages",
    ],
    offerHeading: "What You Get",
    offerLead: "A clear ranked plan to improve Google visibility.",
    offerCards: [
      { title: "Indexing checks", text: "Identify indexing and crawling issues." },
      { title: "Search Console setup", text: "Verify and submit sitemap properly." },
      { title: "Page structure fixes", text: "Align headings and sections to intent." },
      { title: "Service clarity", text: "Create clear pages for what you sell." },
      { title: "Internal linking", text: "Connect pages for stronger understanding." },
      { title: "Priority plan", text: "Ranked actions to apply next." },
    ],
    stepsHeading: "How It Works",
    steps: [
      { title: "Review", text: "Assess indexing and visibility basics." },
      { title: "Plan", text: "Deliver ranked fixes." },
      { title: "Implement", text: "Optional support to apply improvements." },
      { title: "Verify", text: "Confirm indexing and visibility changes." },
    ],
    finalHeading: "Want To Be Found On Google?",
    finalLead: "Send your site link and we will map the simplest visibility fixes first.",
  },

  {
    key: "competitors-outrank-me",
    path: "/competitors-outrank-me",
    title: "Competitors Outrank Me | Improve Local Search | 2merative",
    description:
      "If competitors outrank you, you likely have gaps in service clarity, local signals, trust, or structure. Get a clear plan to improve.",
    heroHeading: "Competitors Outrank You On Google",
    heroSubheading:
      "We find the gaps and show you what to fix first to improve local visibility and enquiries.",
    problemHeading: "Ranking Comes From Relevance, Trust, And Structure",
    problemParagraphs: [
      "Many businesses lose rankings because their pages are unclear and do not match search intent.",
      "Local signals and trust proof are often missing or inconsistent.",
      "We focus on the practical changes that move the needle.",
    ],
    problemBoxHeading: "Common ranking gaps",
    problemBullets: [
      "Services not described clearly",
      "No suburb or service area clarity",
      "Weak internal linking",
      "GBP and website mismatch",
      "Proof and authority missing",
      "Poor mobile experience",
    ],
    offerHeading: "What You Get",
    offerLead: "A ranked plan to close the gaps and improve visibility.",
    offerCards: [
      { title: "Intent mapping", text: "Align pages to real searches customers use." },
      { title: "Service page structure", text: "Fix headings, layout, and clarity." },
      { title: "Local signals", text: "Add service area relevance correctly." },
      { title: "Trust signals", text: "Add proof blocks where it matters." },
      { title: "Internal linking", text: "Connect pages so Google and users understand." },
      { title: "Priority plan", text: "Clear ranked actions and scope options." },
    ],
    stepsHeading: "How It Works",
    steps: [
      { title: "Discovery", text: "Confirm services and target suburbs." },
      { title: "Audit", text: "Review visibility gaps and competitors." },
      { title: "Plan", text: "Deliver ranked fixes." },
      { title: "Implement", text: "Optional support to apply improvements." },
    ],
    finalHeading: "Want To Catch Up And Overtake?",
    finalLead: "Send your link and we will map the simplest visibility improvements first.",
  },

  {
    key: "website-slow-on-mobile",
    path: "/website-slow-on-mobile",
    title: "Website Slow On Mobile | Speed Fix Plan | 2merative",
    description:
      "If your website is slow on mobile, you lose leads. We identify what is slowing it down and give you a clear fix plan.",
    heroHeading: "Your Website Is Slow On Mobile",
    heroSubheading:
      "Slow sites lose enquiries. We identify the speed killers and give you a simple fix plan.",
    problemHeading: "Mobile Speed Is Often The Real Conversion Problem",
    problemParagraphs: [
      "Most visitors arrive on a phone. If the site loads slowly, they leave.",
      "Large images, heavy scripts, and poor caching are common causes.",
      "We focus on fixes that make the biggest difference fast.",
    ],
    problemBoxHeading: "Common speed killers",
    problemBullets: [
      "Oversized images",
      "Too much JavaScript",
      "Heavy fonts and animations",
      "No caching or compression",
      "Third-party scripts",
      "Poor hosting configuration",
    ],
    offerHeading: "What You Get",
    offerLead: "A practical speed fix plan focused on results.",
    offerCards: [
      { title: "Speed audit", text: "Identify the biggest mobile bottlenecks." },
      { title: "Image optimisation", text: "Compress and serve images properly." },
      { title: "Script cleanup", text: "Reduce or defer heavy scripts." },
      { title: "Caching plan", text: "Improve caching and delivery." },
      { title: "Mobile UX checks", text: "Fix layout friction that slows actions." },
      { title: "Priority list", text: "Ranked actions with expected impact." },
    ],
    stepsHeading: "How It Works",
    steps: [
      { title: "Measure", text: "Baseline speed and key pages." },
      { title: "Diagnose", text: "Identify major bottlenecks." },
      { title: "Fix", text: "Implement the highest-impact improvements." },
      { title: "Verify", text: "Re-test and confirm gains." },
    ],
    finalHeading: "Want A Faster Website That Converts Better?",
    finalLead: "Send your link and we will map the simplest speed wins first.",
  },

  {
    key: "pagespeed-score-bad",
    path: "/pagespeed-score-bad",
    title: "Bad PageSpeed Score | Improve Speed And UX | 2merative",
    description:
      "If your PageSpeed score is bad, you can improve it with targeted fixes. We focus on changes that improve real user experience.",
    heroHeading: "Your PageSpeed Score Is Bad",
    heroSubheading:
      "We focus on practical fixes that improve real speed, not just a number.",
    problemHeading: "Scores Matter Less Than Real Mobile Experience",
    problemParagraphs: [
      "A bad score often comes from heavy images, scripts, and render-blocking assets.",
      "The goal is faster loads and smoother interactions for real users.",
      "We apply targeted changes that improve both speed and conversions.",
    ],
    problemBoxHeading: "Common causes of bad scores",
    problemBullets: [
      "Large images",
      "Render-blocking resources",
      "Too many third-party scripts",
      "Unoptimised fonts",
      "Poor caching",
      "Heavy animations",
    ],
    offerHeading: "What You Get",
    offerLead: "A focused plan to improve speed and user experience.",
    offerCards: [
      { title: "Audit", text: "Identify what is causing slowdowns." },
      { title: "Asset optimisation", text: "Fix images, fonts, and critical resources." },
      { title: "Script strategy", text: "Defer or remove heavy scripts." },
      { title: "Caching", text: "Improve delivery with caching and compression." },
      { title: "UX checks", text: "Fix friction that reduces enquiries." },
      { title: "Before and after", text: "Clear proof of what improved." },
    ],
    stepsHeading: "How It Works",
    steps: [
      { title: "Baseline", text: "Measure key pages and issues." },
      { title: "Fix plan", text: "Prioritise changes by impact." },
      { title: "Implement", text: "Apply improvements safely." },
      { title: "Verify", text: "Re-test and confirm results." },
    ],
    finalHeading: "Want Faster Loads And More Enquiries?",
    finalLead: "Send your link and we will map the simplest speed improvements first.",
  },

  {
    key: "mobile-layout-broken",
    path: "/mobile-layout-broken",
    title: "Mobile Layout Broken | Fix Mobile UX | 2merative",
    description:
      "If your mobile layout is broken, you lose leads. We fix spacing, tap targets, overflow issues, and mobile-first usability.",
    heroHeading: "Your Mobile Layout Is Broken",
    heroSubheading:
      "We fix the mobile experience so people can read, tap, and contact you without friction.",
    problemHeading: "Most Customers Visit On A Phone",
    problemParagraphs: [
      "Broken layouts, tiny text, and hard-to-tap buttons cost enquiries.",
      "Mobile problems often come from overflow, spacing, and inconsistent components.",
      "We fix mobile-first UX so the site works for real customers.",
    ],
    problemBoxHeading: "Common mobile issues",
    problemBullets: [
      "Horizontal scrolling",
      "Buttons hard to tap",
      "Text too small",
      "Menus glitching",
      "Images overflowing",
      "Forms unusable",
    ],
    offerHeading: "What You Get",
    offerLead: "A mobile-first fix plan that improves usability and enquiries.",
    offerCards: [
      { title: "Layout fixes", text: "Stop overflow and broken sections." },
      { title: "Tap target upgrades", text: "Make key actions easy to tap." },
      { title: "Typography cleanup", text: "Improve readability on phones." },
      { title: "Nav and menu fixes", text: "Make navigation consistent and reliable." },
      { title: "Form improvements", text: "Make enquiry forms easy to complete." },
      { title: "Testing", text: "Validate across common mobile sizes." },
    ],
    stepsHeading: "How It Works",
    steps: [
      { title: "Identify", text: "Confirm the broken pages and devices." },
      { title: "Fix", text: "Apply layout and CSS corrections." },
      { title: "Test", text: "Check common screen sizes and browsers." },
      { title: "Deploy", text: "Ship fixes safely." },
    ],
    finalHeading: "Want A Mobile Website That Works Properly?",
    finalLead: "Send your link and we will map the simplest mobile fixes first.",
  },

  {
    key: "website-looks-dated",
    path: "/website-looks-dated",
    title: "Website Looks Dated | Modernise Without Overbuilding | 2merative",
    description:
      "If your website looks dated, it can reduce trust and enquiries. We modernise design, clarity, and mobile UX with practical changes.",
    heroHeading: "If Your Website Looks Dated, You Lose Trust",
    heroSubheading:
      "We modernise the look and clarity without pointless complexity. Fast, clean, mobile-first.",
    problemHeading: "First Impressions Decide If People Enquire",
    problemParagraphs: [
      "A dated website can make a good business look risky or inactive.",
      "Modern does not mean fancy. It means clear, clean, and easy to use.",
      "We update what matters most for trust and conversion.",
    ],
    problemBoxHeading: "Common dated signals",
    problemBullets: [
      "Old layout and fonts",
      "Weak headings and structure",
      "No modern proof blocks",
      "Mobile feels clunky",
      "Slow pages",
      "Confusing navigation",
    ],
    offerHeading: "What You Get",
    offerLead: "A modern site experience designed for trust and enquiries.",
    offerCards: [
      { title: "Modern layout", text: "Clean spacing, better hierarchy, clear sections." },
      { title: "Copy clarity", text: "Sharper messaging that matches customer intent." },
      { title: "Trust signals", text: "Reviews, proof, and credibility blocks." },
      { title: "Mobile-first UX", text: "Better usability on phones." },
      { title: "Speed wins", text: "Faster load times where it matters." },
      { title: "Scope options", text: "Fix, refresh, or rebuild with clear choices." },
    ],
    stepsHeading: "How It Works",
    steps: [
      { title: "Review", text: "Identify trust and conversion issues." },
      { title: "Plan", text: "Define the simplest upgrade path." },
      { title: "Refresh", text: "Implement high-impact improvements." },
      { title: "Launch", text: "Test mobile and contact flow." },
    ],
    finalHeading: "Want A Website That Looks Credible Again?",
    finalLead: "Send your link and we will map the simplest modernisation plan.",
  },

  {
    key: "website-looks-scammy",
    path: "/website-looks-scammy",
    title: "Website Looks Scammy | Fix Trust Signals | 2merative",
    description:
      "If your website looks scammy, visitors will not contact you. We fix trust signals, clarity, proof, and design consistency.",
    heroHeading: "If Your Website Looks Scammy, People Will Not Enquire",
    heroSubheading:
      "We improve trust signals, design consistency, and clarity so customers feel safe contacting you.",
    problemHeading: "Trust Is The Real Conversion Driver",
    problemParagraphs: [
      "Even good businesses lose leads if the website feels unclear, inconsistent, or low-quality.",
      "Trust signals and proof need to be visible, not hidden.",
      "We fix the credibility gaps that stop enquiries.",
    ],
    problemBoxHeading: "Common trust problems",
    problemBullets: [
      "No clear business identity",
      "Inconsistent branding",
      "Missing proof and reviews",
      "Weak contact details",
      "Bad mobile experience",
      "Poor page structure",
    ],
    offerHeading: "What You Get",
    offerLead: "A credibility-focused fix plan that improves enquiries.",
    offerCards: [
      { title: "Identity clarity", text: "Make it obvious who you are and what you do." },
      { title: "Proof blocks", text: "Reviews, work examples, and credibility signals." },
      { title: "Design consistency", text: "Clean, modern layout and typography." },
      { title: "Contact confidence", text: "Clear phone, email, address, and CTAs." },
      { title: "Mobile trust", text: "Fix mobile UX and readability." },
      { title: "Priority plan", text: "Ranked actions with clear scope options." },
    ],
    stepsHeading: "How It Works",
    steps: [
      { title: "Audit", text: "Identify trust gaps and friction points." },
      { title: "Plan", text: "Deliver the simplest credibility upgrade path." },
      { title: "Implement", text: "Apply updates if you want us to do it." },
      { title: "Verify", text: "Test conversion path and contact actions." },
    ],
    finalHeading: "Want A Website People Trust?",
    finalLead: "Send your link and we will map the simplest credibility fixes first.",
  },

  {
    key: "only-have-facebook-page",
    path: "/only-have-facebook-page",
    title: "Only Have A Facebook Page | Own Your Online Presence | 2merative",
    description:
      "If your main online presence is a Facebook page, you do not own your platform. Build a website you control that drives enquiries.",
    heroHeading: "If You Only Have A Facebook Page, You Do Not Own Your Presence",
    heroSubheading:
      "Platforms change. Accounts get restricted. A website gives you a stable place customers can always find.",
    problemHeading: "Social Is Not A Website",
    problemParagraphs: [
      "Social pages are useful, but you do not control the rules, reach, or access.",
      "Your website is your stable home for services, proof, and enquiries.",
      "We build simple websites that support your business long-term.",
    ],
    problemBoxHeading: "Common risks of relying on social only",
    problemBullets: [
      "Reach drops without warning",
      "Account restrictions",
      "No structured service pages",
      "Hard to rank locally",
      "Hard to track leads",
      "Customers cannot find key info quickly",
    ],
    offerHeading: "What You Get",
    offerLead: "A website you own that supports enquiries and trust.",
    offerCards: [
      { title: "Clear services", text: "Explain what you do in a way customers understand." },
      { title: "Trust signals", text: "Show proof, reviews, and credibility." },
      { title: "Simple CTAs", text: "Make calling and messaging easy." },
      { title: "Local visibility", text: "Structure built for local search." },
      { title: "Mobile-first", text: "Designed for phones first." },
      { title: "Platform resilience", text: "A stable online home you control." },
    ],
    stepsHeading: "How It Works",
    steps: [
      { title: "Quick chat", text: "Confirm services and goals." },
      { title: "Plan", text: "Outline pages and conversion path." },
      { title: "Build", text: "Design and implement quickly." },
      { title: "Launch", text: "Test and go live." },
    ],
    finalHeading: "Ready To Own Your Online Presence?",
    finalLead: "Send your social link and we will map a simple website plan.",
  },

  {
    key: "facebook-reach-dropped",
    path: "/facebook-reach-dropped",
    title: "Facebook Reach Dropped | Build A Stable Website | 2merative",
    description:
      "If Facebook reach dropped and leads slowed, you need a stable online home you control. Build a website that stays visible.",
    heroHeading: "Facebook Reach Dropped And Leads Slowed",
    heroSubheading:
      "A website gives you a stable home for customers, services, and enquiries. You control it.",
    problemHeading: "Platforms Change. Your Website Stays.",
    problemParagraphs: [
      "Algorithm shifts and policy changes can reduce reach overnight.",
      "A website protects your visibility and gives customers a clear place to land.",
      "We build websites that convert, not just look nice.",
    ],
    problemBoxHeading: "Common symptoms",
    problemBullets: [
      "Posts get fewer views",
      "Messages dropped off",
      "Enquiries slowed",
      "Customers cannot find key info",
      "No clear funnel",
      "No lead tracking",
    ],
    offerHeading: "What You Get",
    offerLead: "A stable online home that drives enquiries.",
    offerCards: [
      { title: "Clear landing pages", text: "A place customers can always find you." },
      { title: "Trust signals", text: "Proof and reviews presented properly." },
      { title: "Fast contact path", text: "Clear CTAs for calls and messages." },
      { title: "Local visibility", text: "Structure to support local searches." },
      { title: "Mobile-first", text: "Designed for phones first." },
      { title: "Simple plan", text: "Clear steps with no confusion." },
    ],
    stepsHeading: "How It Works",
    steps: [
      { title: "Discovery", text: "Confirm services and ideal customers." },
      { title: "Structure", text: "Map pages and conversion flow." },
      { title: "Build", text: "Implement and refine." },
      { title: "Launch", text: "Test contact paths and go live." },
    ],
    finalHeading: "Want Leads That Do Not Depend On Facebook?",
    finalLead: "Send your link and we will map a simple website plan.",
  },

  {
    key: "fix-vs-rebuild",
    path: "/fix-vs-rebuild",
    title: "Fix Vs Rebuild | Choose The Right Website Path | 2merative",
    description:
      "Should you fix your current website or rebuild? Get a clear comparison and a recommended path based on cost, speed, and impact.",
    heroHeading: "Fix Your Website Or Rebuild It?",
    heroSubheading:
      "We help you choose the simplest path that improves enquiries and trust without wasting money.",
    problemHeading: "The Wrong Choice Costs Time And Money",
    problemParagraphs: [
      "Sometimes a site needs a refresh. Sometimes it needs a rebuild.",
      "The answer depends on structure, speed, content clarity, and technical constraints.",
      "We assess your site and recommend the best path.",
    ],
    problemBoxHeading: "Signs you can fix",
    problemBullets: [
      "Pages and structure mostly work",
      "Speed can be improved",
      "Design just needs modernising",
      "Copy needs clarity",
      "Forms need fixes",
      "Minor SEO structure gaps",
    ],
    offerHeading: "What You Get",
    offerLead: "A clear decision and a plan you can act on.",
    offerCards: [
      { title: "Site assessment", text: "Identify what is working and what is not." },
      { title: "Fix plan", text: "If a fix is viable, you get a ranked upgrade list." },
      { title: "Rebuild plan", text: "If rebuild is better, you get a simple scope outline." },
      { title: "Cost clarity", text: "Clear options without confusion." },
      { title: "Conversion focus", text: "Decisions tied to enquiries, not fluff." },
      { title: "Next steps", text: "A simple plan to move forward." },
    ],
    stepsHeading: "How It Works",
    steps: [
      { title: "Review", text: "Assess the current site and goals." },
      { title: "Compare", text: "Fix vs rebuild options with trade-offs." },
      { title: "Plan", text: "Define the chosen path." },
      { title: "Implement", text: "Optional build support." },
    ],
    finalHeading: "Want The Simplest Path To Better Enquiries?",
    finalLead: "Send your site link and we will recommend fix or rebuild.",
  },

  {
    key: "conversion-audit",
    path: "/conversion-audit",
    title: "Conversion Audit | Turn Visitors Into Enquiries | 2merative",
    description:
      "A conversion audit to find what stops visitors from contacting you. Get a clear fix plan focused on enquiries and trust.",
    heroHeading: "Conversion Audit That Finds What Stops Enquiries",
    heroSubheading:
      "We identify friction and confusion in your website path, then give you a clear fix plan.",
    problemHeading: "Small Friction Points Kill Enquiries",
    problemParagraphs: [
      "Visitors do not read everything. They scan and decide quickly.",
      "If the message, proof, and next step are not obvious, they leave.",
      "This audit focuses on high-impact conversion fixes.",
    ],
    problemBoxHeading: "Common conversion gaps",
    problemBullets: [
      "Weak hero message",
      "No clear CTAs",
      "Proof missing",
      "Contact friction",
      "Mobile readability issues",
      "Too many distractions",
    ],
    offerHeading: "What You Get",
    offerLead: "A practical conversion fix plan.",
    offerCards: [
      { title: "Page review", text: "Assess key pages that drive leads." },
      { title: "CTA mapping", text: "Fix next steps and placement." },
      { title: "Trust upgrades", text: "Add proof and credibility blocks." },
      { title: "Mobile UX fixes", text: "Improve phone experience." },
      { title: "Form and contact checks", text: "Ensure enquiries work end to end." },
      { title: "Priority plan", text: "Ranked actions with scope options." },
    ],
    stepsHeading: "How It Works",
    steps: [
      { title: "Discovery", text: "Confirm goals and current issues." },
      { title: "Audit", text: "Review conversion path and pages." },
      { title: "Plan", text: "Deliver ranked improvements." },
      { title: "Implement", text: "Optional support to apply changes." },
    ],
    finalHeading: "Want More Enquiries Without More Traffic?",
    finalLead: "Send your link and we will map the simplest conversion improvements.",
  },

  {
    key: "landing-page-build",
    path: "/landing-page-build",
    title: "Landing Page Build | One Page That Converts | 2merative",
    description:
      "A landing page build designed to convert. Clear message, trust signals, and a single focused call to action.",
    heroHeading: "Landing Page Build That Converts Visitors Into Enquiries",
    heroSubheading:
      "One focused page. Clear message. Proof. Simple next step.",
    problemHeading: "Most Websites Try To Do Too Much",
    problemParagraphs: [
      "A landing page works when it focuses on one offer and one action.",
      "It is perfect for ads, referrals, and specific services.",
      "We build landing pages that guide visitors to contact you.",
    ],
    problemBoxHeading: "Common landing page mistakes",
    problemBullets: [
      "Too many CTAs",
      "Weak headline",
      "No proof",
      "Too much text",
      "Mobile friction",
      "Slow load",
    ],
    offerHeading: "What You Get",
    offerLead: "A focused page designed for enquiries.",
    offerCards: [
      { title: "Headline and offer clarity", text: "Clear message that matches intent." },
      { title: "Proof blocks", text: "Reviews, examples, and trust signals." },
      { title: "CTA design", text: "One clear next step." },
      { title: "Mobile-first layout", text: "Easy to read and act on." },
      { title: "Speed focus", text: "Quick load and smooth UX." },
      { title: "Tracking ready", text: "Conversion tracking supported." },
    ],
    stepsHeading: "How It Works",
    steps: [
      { title: "Discovery", text: "Confirm offer and audience." },
      { title: "Draft", text: "Outline structure and copy." },
      { title: "Build", text: "Design and implement." },
      { title: "Launch", text: "Test conversion path and go live." },
    ],
    finalHeading: "Want One Page That Gets Enquiries?",
    finalLead: "Send your offer and we will map the simplest landing page plan.",
  },

  {
    key: "service-page-pack",
    path: "/service-page-pack",
    title: "Service Page Pack | Add Pages That Rank And Convert | 2merative",
    description:
      "A service page pack that adds clear pages for what you sell. Improve local intent visibility and conversions.",
    heroHeading: "Service Page Pack That Improves Visibility And Enquiries",
    heroSubheading:
      "Clear pages for what you actually sell, built to match local search intent and convert enquiries.",
    problemHeading: "One Generic Page Does Not Rank Or Convert Well",
    problemParagraphs: [
      "Most businesses list everything on one page and wonder why Google and customers are confused.",
      "Dedicated service pages improve clarity and visibility.",
      "We build service pages that match how people search.",
    ],
    problemBoxHeading: "Common service page gaps",
    problemBullets: [
      "One vague services page",
      "No clear service names",
      "No service area context",
      "Weak internal linking",
      "No proof per service",
      "No clear CTAs",
    ],
    offerHeading: "What You Get",
    offerLead: "A set of service pages with clear structure and CTAs.",
    offerCards: [
      { title: "Service page structure", text: "Clear headings and sections." },
      { title: "Intent alignment", text: "Match how customers search." },
      { title: "Proof blocks", text: "Add credibility where it matters." },
      { title: "Internal linking", text: "Connect pages properly." },
      { title: "Mobile-first layout", text: "Easy to read and act on." },
      { title: "CTA consistency", text: "Clear next steps on every page." },
    ],
    stepsHeading: "How It Works",
    steps: [
      { title: "Discovery", text: "Confirm services and priorities." },
      { title: "Plan", text: "Map the page set." },
      { title: "Build", text: "Write and implement pages." },
      { title: "Launch", text: "Test and publish." },
    ],
    finalHeading: "Want Service Pages That Actually Work?",
    finalLead: "Send your services and we will recommend the best page pack.",
  },

  {
    key: "local-seo-starter-pack",
    path: "/local-seo-starter-pack",
    title: "Local SEO Starter Pack | Get Found Locally | 2merative",
    description:
      "A local SEO starter pack focused on the basics that drive enquiries. Service clarity, local signals, and trust improvements.",
    heroHeading: "Local SEO Starter Pack That Drives Real Enquiries",
    heroSubheading:
      "Practical local SEO without the fluff. Fix the basics that improve visibility and calls.",
    problemHeading: "Local SEO Fails When The Basics Are Missing",
    problemParagraphs: [
      "Most businesses do not need complex SEO. They need clarity, consistency, and trust.",
      "Your website and Google profile should reinforce each other.",
      "This pack focuses on high-impact foundational improvements.",
    ],
    problemBoxHeading: "Common local SEO gaps",
    problemBullets: [
      "Services unclear",
      "No service area clarity",
      "Weak GBP alignment",
      "No trust signals",
      "Poor page structure",
      "No internal linking",
    ],
    offerHeading: "What You Get",
    offerLead: "A foundational plan to lift local visibility and enquiries.",
    offerCards: [
      { title: "Service structure", text: "Clarify services with proper pages." },
      { title: "Local signals", text: "Add service area relevance correctly." },
      { title: "GBP alignment", text: "Ensure profile and site match." },
      { title: "Trust upgrades", text: "Add proof blocks that build confidence." },
      { title: "Internal linking", text: "Improve site structure for clarity." },
      { title: "Priority plan", text: "Ranked actions to take next." },
    ],
    stepsHeading: "How It Works",
    steps: [
      { title: "Discovery", text: "Confirm services and target suburbs." },
      { title: "Audit", text: "Review site and profile gaps." },
      { title: "Implement", text: "Apply key improvements." },
      { title: "Track", text: "Confirm movement in visibility." },
    ],
    finalHeading: "Want More Local Enquiries?",
    finalLead: "Send your link and we will map a practical local SEO starter plan.",
  },

  {
    key: "ga4-setup",
    path: "/ga4-setup",
    title: "GA4 Setup | Track Enquiries Properly | 2merative",
    description:
      "Set up GA4 properly so you can see where enquiries come from. Track calls, forms, and key actions with clear reporting.",
    heroHeading: "GA4 Setup So You Can Track Enquiries Properly",
    heroSubheading:
      "Stop guessing. Track leads and key actions so you know what works.",
    problemHeading: "If You Cannot Track Leads, You Cannot Improve",
    problemParagraphs: [
      "Many sites have analytics installed but no useful events.",
      "You need tracking that matches real enquiries.",
      "We set up clear lead tracking and simple reporting.",
    ],
    problemBoxHeading: "Common tracking problems",
    problemBullets: [
      "GA4 installed but no events",
      "No form tracking",
      "No call tracking",
      "Bad traffic attribution",
      "Multiple tags firing",
      "No clear reporting view",
    ],
    offerHeading: "What You Get",
    offerLead: "Clear tracking for the actions that matter.",
    offerCards: [
      { title: "GA4 install", text: "Clean setup with correct configuration." },
      { title: "Event tracking", text: "Track forms, clicks, and key actions." },
      { title: "Conversion setup", text: "Mark real enquiry events as conversions." },
      { title: "Search Console link", text: "Connect search visibility data." },
      { title: "Simple reporting", text: "A clear way to read performance." },
      { title: "QA", text: "Verify data is firing correctly." },
    ],
    stepsHeading: "How It Works",
    steps: [
      { title: "Discovery", text: "Confirm what counts as a lead." },
      { title: "Setup", text: "Install and configure GA4." },
      { title: "Track", text: "Add events and conversions." },
      { title: "Verify", text: "Test and confirm reporting." },
    ],
    finalHeading: "Want To Know Where Your Leads Come From?",
    finalLead: "Send your link and we will map a clean tracking setup.",
  },

  {
    key: "search-console-setup",
    path: "/search-console-setup",
    title: "Google Search Console Setup | Improve Visibility | 2merative",
    description:
      "Set up Google Search Console to track indexing, queries, and visibility. Fix common issues that stop pages from appearing.",
    heroHeading: "Google Search Console Setup That Gives You Clarity",
    heroSubheading:
      "Track what Google sees, what people search, and what needs fixing for better visibility.",
    problemHeading: "You Cannot Fix Visibility Without Data",
    problemParagraphs: [
      "Search Console shows indexing status and search queries.",
      "Without it, you are guessing why you are not visible.",
      "We set it up properly and highlight the key issues.",
    ],
    problemBoxHeading: "Common Search Console gaps",
    problemBullets: [
      "Not verified",
      "No sitemap submitted",
      "Pages not indexed",
      "Indexing errors ignored",
      "Wrong canonical signals",
      "No query visibility insights",
    ],
    offerHeading: "What You Get",
    offerLead: "A clean setup and a simple visibility checklist.",
    offerCards: [
      { title: "Verification", text: "Correct verification setup." },
      { title: "Sitemap setup", text: "Submit and validate sitemap." },
      { title: "Indexing checks", text: "Identify pages missing from Google." },
      { title: "Issue review", text: "Highlight key errors and warnings." },
      { title: "Query insights", text: "See what searches drive clicks." },
      { title: "Priority plan", text: "Ranked actions to improve visibility." },
    ],
    stepsHeading: "How It Works",
    steps: [
      { title: "Verify", text: "Confirm ownership and access." },
      { title: "Submit", text: "Add sitemap and key settings." },
      { title: "Review", text: "Check indexing and visibility." },
      { title: "Plan", text: "Deliver next-step actions." },
    ],
    finalHeading: "Want To Know What Google Sees?",
    finalLead: "Send your link and we will set up Search Console properly.",
  },

  {
    key: "tracking-broken-after-update",
    path: "/tracking-broken-after-update",
    title: "Tracking Broken After Updates | Fix Analytics | 2merative",
    description:
      "If tracking broke after updates, you may have lost conversion data. We fix GA4, tags, and events so reporting works again.",
    heroHeading: "Tracking Broke After Updates",
    heroSubheading:
      "We restore clean tracking so you can trust your lead data again.",
    problemHeading: "Small Changes Can Break Tracking Quietly",
    problemParagraphs: [
      "Theme changes, script changes, consent settings, or tag duplication can break events.",
      "Often everything looks fine until you realise conversions stopped.",
      "We identify the cause and restore correct tracking.",
    ],
    problemBoxHeading: "Common tracking failures",
    problemBullets: [
      "Events stopped firing",
      "Duplicate tags",
      "Consent changes",
      "Form tracking broken",
      "Wrong measurement ID",
      "SPA routing not tracked",
    ],
    offerHeading: "What You Get",
    offerLead: "Clean analytics and conversion tracking restored.",
    offerCards: [
      { title: "Audit", text: "Find what changed and what broke." },
      { title: "Fix", text: "Restore events and conversions." },
      { title: "Clean-up", text: "Remove duplicates and conflicts." },
      { title: "Verify", text: "Confirm events fire correctly." },
      { title: "Reporting", text: "Ensure conversions are visible." },
      { title: "Notes", text: "Simple summary of what changed." },
    ],
    stepsHeading: "How It Works",
    steps: [
      { title: "Baseline", text: "Check current tracking state." },
      { title: "Diagnose", text: "Identify the breaking change." },
      { title: "Repair", text: "Fix configuration and events." },
      { title: "Verify", text: "Test and confirm." },
    ],
    finalHeading: "Want Accurate Lead Data Again?",
    finalLead: "Send your link and we will restore clean tracking.",
  },

  {
    key: "google-business-profile-not-getting-calls",
    path: "/google-business-profile-not-getting-calls",
    title: "Google Business Profile Not Getting Calls | Fix Local Visibility | 2merative",
    description:
      "If your Google Business Profile is not getting calls, something is missing. We improve the profile, website alignment, and conversion path.",
    heroHeading: "Your Google Business Profile Is Not Getting Calls",
    heroSubheading:
      "We find what is missing in local visibility, trust, and the path to contact. Then we give you a clear fix plan.",
    problemHeading: "Local Calls Come From Relevance And Trust",
    problemParagraphs: [
      "GBP performance depends on category choices, service clarity, content, reviews, and consistent business details.",
      "If the website and profile do not match, you lose relevance and confidence.",
      "We focus on practical improvements that drive calls.",
    ],
    problemBoxHeading: "Common GBP issues",
    problemBullets: [
      "Wrong or weak categories",
      "Services not described properly",
      "No local relevance signals",
      "Photos outdated or missing",
      "Inconsistent business details",
      "Weak website conversion path",
    ],
    offerHeading: "What You Get",
    offerLead: "A clear plan to increase calls from Google Maps.",
    offerCards: [
      { title: "Profile cleanup", text: "Fix categories, services, and key fields." },
      { title: "Website alignment", text: "Match services and messaging across both." },
      { title: "Trust signals", text: "Add proof and clarity that improves confidence." },
      { title: "Local intent structure", text: "Support suburb and service searches." },
      { title: "Conversion path", text: "Make call and enquiry actions obvious." },
      { title: "Priority plan", text: "Clear ranked actions and next steps." },
    ],
    stepsHeading: "How It Works",
    steps: [
      { title: "Review", text: "Assess profile and website alignment." },
      { title: "Fix list", text: "Deliver a ranked improvement plan." },
      { title: "Implement", text: "Optional support to apply changes." },
      { title: "Track", text: "Confirm actions that improve calls." },
    ],
    finalHeading: "Want More Calls From Google Maps?",
    finalLead: "Send your profile link and website link and we will map the simplest improvements first.",
  },

  {
    key: "google-maps-not-showing",
    path: "/google-maps-not-showing",
    title: "Not Showing On Google Maps | Fix Your GBP | 2merative",
    description:
      "If you are not showing on Google Maps, your Google Business Profile may be missing key signals. Get a clear fix plan.",
    heroHeading: "Your Business Is Not Showing On Google Maps",
    heroSubheading:
      "We identify what is missing in your Google Business Profile and website alignment, then give you a clear fix plan.",
    problemHeading: "Maps Visibility Depends On Setup And Relevance",
    problemParagraphs: [
      "Map visibility relies on categories, services, location signals, and consistent details.",
      "If your profile is incomplete or mismatched with your website, you lose relevance.",
      "We focus on practical changes that improve visibility and calls.",
    ],
    problemBoxHeading: "Common reasons you do not appear",
    problemBullets: [
      "Wrong categories",
      "Profile incomplete",
      "Service areas unclear",
      "Inconsistent business details",
      "Weak website alignment",
      "Low proof and authority signals",
    ],
    offerHeading: "What You Get",
    offerLead: "A clear plan to improve Maps visibility and calls.",
    offerCards: [
      { title: "Profile review", text: "Fix key setup fields that affect visibility." },
      { title: "Service clarity", text: "Ensure services match real searches." },
      { title: "Website alignment", text: "Make the site and profile support each other." },
      { title: "Trust signals", text: "Improve credibility and confidence." },
      { title: "Local relevance", text: "Support suburb and service intent." },
      { title: "Priority plan", text: "Ranked actions to take next." },
    ],
    stepsHeading: "How It Works",
    steps: [
      { title: "Check", text: "Review profile fields and visibility." },
      { title: "Fix list", text: "Deliver ranked actions." },
      { title: "Implement", text: "Optional support to apply updates." },
      { title: "Track", text: "Confirm movement in calls and views." },
    ],
    finalHeading: "Want To Show Up On Google Maps?",
    finalLead: "Send your profile link and we will map the simplest fixes first.",
  },

  {
    key: "not-sure-what-to-fix-first",
    path: "/not-sure-what-to-fix-first",
    title: "Not Sure What To Fix First | Get A Clear Priority Plan | 2merative",
    description:
      "If you are not sure what to fix first, you need a clear ranked plan. We identify the biggest impact changes for enquiries and visibility.",
    heroHeading: "Not Sure What To Fix First?",
    heroSubheading:
      "We give you a clear ranked plan so you stop guessing and start fixing what matters.",
    problemHeading: "Most Businesses Do Not Need More Random Work",
    problemParagraphs: [
      "You do not need ten new ideas. You need the right three fixes first.",
      "Without priorities, you waste time on low-impact tasks.",
      "We create a simple plan based on enquiries, trust, and visibility.",
    ],
    problemBoxHeading: "Common situations",
    problemBullets: [
      "Enquiries slowed",
      "Competitors outrank you",
      "Site feels outdated",
      "Mobile experience poor",
      "Google profile inconsistent",
      "No clear conversion path",
    ],
    offerHeading: "What You Get",
    offerLead: "A ranked action plan with clear next steps.",
    offerCards: [
      { title: "Quick audit", text: "Review the key pages and visibility signals." },
      { title: "Priority list", text: "Top fixes ranked by impact." },
      { title: "Quick wins", text: "Fast changes that lift enquiries." },
      { title: "Clarity upgrades", text: "Improve messaging and CTAs." },
      { title: "Trust upgrades", text: "Add proof where it matters." },
      { title: "Scope options", text: "Fix, refresh, or rebuild with clear choices." },
    ],
    stepsHeading: "How It Works",
    steps: [
      { title: "Discovery", text: "Confirm goals and current issues." },
      { title: "Audit", text: "Review conversion and visibility basics." },
      { title: "Plan", text: "Deliver ranked actions." },
      { title: "Support", text: "Optional help to implement fixes." },
    ],
    finalHeading: "Want A Clear Plan In Plain English?",
    finalLead: "Send your site link and we will map the best next steps.",
  },

  {
    key: "website-speed-fix",
    path: "/website-speed-fix",
    title: "Website Speed Fix | Make Your Site Faster | 2merative",
    description:
      "Website speed fix for slow sites. Improve mobile load times with practical performance changes that help conversions.",
    heroHeading: "Website Speed Fix That Improves Conversions",
    heroSubheading:
      "We focus on the changes that make the biggest speed difference on mobile.",
    problemHeading: "Speed Is A Sales Problem",
    problemParagraphs: [
      "Slow sites lose leads. People do not wait.",
      "Speed issues often come from images, scripts, and delivery setup.",
      "We fix the bottlenecks that matter most.",
    ],
    problemBoxHeading: "Common speed issues",
    problemBullets: [
      "Images too large",
      "No compression",
      "Too many scripts",
      "Heavy fonts",
      "Poor caching",
      "Slow third-party embeds",
    ],
    offerHeading: "What You Get",
    offerLead: "A focused speed improvement plan with real impact.",
    offerCards: [
      { title: "Baseline testing", text: "Measure key pages and bottlenecks." },
      { title: "Image optimisation", text: "Compress and serve correctly." },
      { title: "Script cleanup", text: "Defer or remove heavy scripts." },
      { title: "Caching plan", text: "Improve delivery performance." },
      { title: "UX checks", text: "Remove friction that slows actions." },
      { title: "Verification", text: "Re-test and confirm improvement." },
    ],
    stepsHeading: "How It Works",
    steps: [
      { title: "Measure", text: "Baseline performance." },
      { title: "Diagnose", text: "Identify biggest issues." },
      { title: "Fix", text: "Apply improvements safely." },
      { title: "Verify", text: "Confirm gains." },
    ],
    finalHeading: "Want A Faster Site That Gets More Leads?",
    finalLead: "Send your link and we will map the best speed wins first.",
  },

  {
    key: "website-maintenance-for-small-business",
    path: "/website-maintenance-for-small-business",
    title: "Website Maintenance for Small Business | Keep It Working | 2merative",
    description:
      "Website maintenance for small business to keep forms working, content updated, and performance healthy. Simple ongoing support options.",
    heroHeading: "Website Maintenance That Keeps Your Site Working",
    heroSubheading:
      "Reduce breakages, keep content current, and maintain trust. Simple support options for small business.",
    problemHeading: "Websites Drift Out Of Date Without Maintenance",
    problemParagraphs: [
      "Forms break, plugins update, emails fail, and content gets stale.",
      "Small issues can quietly reduce enquiries.",
      "We keep the basics healthy so your website stays reliable.",
    ],
    problemBoxHeading: "Common maintenance problems",
    problemBullets: [
      "Forms stop delivering",
      "Content is outdated",
      "Broken links",
      "Slowdown over time",
      "Tracking broken",
      "Security updates ignored",
    ],
    offerHeading: "What You Get",
    offerLead: "Ongoing support that keeps your website working.",
    offerCards: [
      { title: "Health checks", text: "Regular checks for issues and breakages." },
      { title: "Content updates", text: "Keep key pages current and clear." },
      { title: "Form monitoring", text: "Ensure enquiries are delivered properly." },
      { title: "Performance checks", text: "Maintain speed and usability." },
      { title: "Tracking checks", text: "Confirm analytics and conversion tracking." },
      { title: "Simple reporting", text: "Clear updates on what changed." },
    ],
    stepsHeading: "How It Works",
    steps: [
      { title: "Baseline", text: "Confirm what needs maintaining." },
      { title: "Plan", text: "Choose a support approach that fits." },
      { title: "Maintain", text: "Apply updates and checks." },
      { title: "Improve", text: "Fix issues as they appear." },
    ],
    finalHeading: "Want A Website You Do Not Have To Worry About?",
    finalLead: "Send your link and we will suggest the simplest maintenance plan.",
  },

  {
    key: "website-care-plans",
    path: "/website-care-plans",
    title: "Website Care Plans | Simple Ongoing Support | 2merative",
    description:
      "Website care plans to keep your website fast, secure, and converting. Practical ongoing support for small business sites.",
    heroHeading: "Website Care Plans That Keep Your Site Healthy",
    heroSubheading:
      "Simple ongoing support for updates, fixes, and reliability. Built for small business owners.",
    problemHeading: "When The Site Breaks, Leads Disappear",
    problemParagraphs: [
      "Most business owners only notice problems when enquiries drop.",
      "A care plan keeps the basics maintained and reduces surprises.",
      "We keep it practical and focused on conversion.",
    ],
    problemBoxHeading: "What care plans prevent",
    problemBullets: [
      "Broken forms and emails",
      "Outdated content",
      "Slow performance",
      "Security issues",
      "Tracking failures",
      "Small bugs becoming big problems",
    ],
    offerHeading: "What You Get",
    offerLead: "Practical ongoing support without fluff.",
    offerCards: [
      { title: "Regular checks", text: "Spot issues early before they cost leads." },
      { title: "Fix support", text: "Handle small changes and quick fixes." },
      { title: "Performance checks", text: "Keep pages fast and smooth." },
      { title: "Content updates", text: "Keep key pages current and clear." },
      { title: "Tracking checks", text: "Confirm leads can be measured." },
      { title: "Clear communication", text: "Simple updates and next steps." },
    ],
    stepsHeading: "How It Works",
    steps: [
      { title: "Baseline", text: "Confirm what is in scope." },
      { title: "Schedule", text: "Set the check cadence." },
      { title: "Maintain", text: "Apply updates and fixes." },
      { title: "Improve", text: "Add improvements as needed." },
    ],
    finalHeading: "Want Ongoing Support That Makes Sense?",
    finalLead: "Send your link and we will recommend a care plan approach.",
  },

  {
    key: "website-pay-monthly",
    path: "/website-pay-monthly",
    title: "Website Pay Monthly | Simple Build Options | 2merative",
    description:
      "Pay monthly website options for small business. A clear, simple path to get a professional website without a large upfront cost.",
    heroHeading: "Website Pay Monthly Options For Small Business",
    heroSubheading:
      "A practical way to get a professional site without a big upfront cost. Clear terms and clear next steps.",
    problemHeading: "Upfront Cost Stops Good Businesses From Getting A Proper Website",
    problemParagraphs: [
      "Many businesses delay because they do not want a large upfront spend.",
      "A monthly option can remove the barrier and help you get visible sooner.",
      "We keep the offer simple and focused on a site that converts enquiries.",
    ],
    problemBoxHeading: "This works best if you",
    problemBullets: [
      "Need a new site soon",
      "Want predictable monthly costs",
      "Want a clear scope with support",
      "Want a website that drives enquiries",
      "Do not want a complex build",
      "Want a simple ongoing plan",
    ],
    offerHeading: "What You Get",
    offerLead: "A clear monthly website option that makes sense for small business.",
    offerCards: [
      { title: "Clear page set", text: "A practical set of pages built for enquiries." },
      { title: "Mobile-first build", text: "Fast, clean, and easy to use on phones." },
      { title: "Trust blocks", text: "Proof signals placed where visitors decide." },
      { title: "Conversion CTAs", text: "Clear contact actions across the site." },
      { title: "Basic tracking setup", text: "Know what is working and what is not." },
      { title: "Support options", text: "Ongoing help to keep it running well." },
    ],
    stepsHeading: "How It Works",
    steps: [
      { title: "Quick chat", text: "Confirm services, goals, and the best plan." },
      { title: "Plan", text: "Lock the page list and key content." },
      { title: "Build", text: "Design and build with fast feedback." },
      { title: "Launch", text: "Go live and confirm contact path." },
    ],
    finalHeading: "Want A Website Without A Big Upfront Cost?",
    finalLead: "Message us and we will explain the monthly option clearly.",
  },

  {
    key: "website-pay-weekly",
    path: "/website-pay-weekly",
    title: "Website Pay Weekly | Get Online Faster | 2merative",
    description:
      "Pay weekly website options for small business. Get a clear website plan without a large upfront payment.",
    heroHeading: "Website Pay Weekly So You Can Get Online Faster",
    heroSubheading:
      "A simple weekly option for businesses that want to launch quickly and keep costs predictable.",
    problemHeading: "Delays Cost Enquiries",
    problemParagraphs: [
      "If your website is overdue, you are losing calls and trust every week.",
      "Weekly payments can make it easier to start now instead of later.",
      "We keep the scope clear and the build practical.",
    ],
    problemBoxHeading: "This helps if you",
    problemBullets: [
      "Need to launch quickly",
      "Want smaller weekly costs",
      "Want a simple, clear scope",
      "Want better enquiry flow",
      "Do not want a complicated site",
      "Need a clean, modern presence",
    ],
    offerHeading: "What You Get",
    offerLead: "A practical website built to drive enquiries.",
    offerCards: [
      { title: "Launch-focused scope", text: "Pages that matter, no fluff." },
      { title: "Mobile-first build", text: "Fast and readable on phones." },
      { title: "Clear CTAs", text: "Call and contact actions that are obvious." },
      { title: "Trust sections", text: "Proof and credibility blocks included." },
      { title: "Basic SEO structure", text: "Clean headings and page structure." },
      { title: "Support option", text: "Help after launch if you want it." },
    ],
    stepsHeading: "How It Works",
    steps: [
      { title: "Quick chat", text: "Confirm goals and best plan." },
      { title: "Scope", text: "Lock the page list and key messaging." },
      { title: "Build", text: "Design and build quickly." },
      { title: "Launch", text: "Go live and test enquiries." },
    ],
    finalHeading: "Want To Get Online This Month?",
    finalLead: "Message us and we will explain the weekly option clearly.",
  },

  {
    key: "website-no-payment-up-front",
    path: "/website-no-payment-up-front",
    title: "Website With No Payment Up Front | Application Option | 2merative",
    description:
      "A no payment up front website option for eligible small businesses. Clear scope, clear milestones, and a simple path to launch.",
    heroHeading: "Website With No Payment Up Front",
    heroSubheading:
      "For eligible businesses only. Clear scope. Clear milestones. Built to drive enquiries.",
    problemHeading: "Some Businesses Need Momentum More Than Perfection",
    problemParagraphs: [
      "If you need a proper website but cash flow is tight, an application-based option can help.",
      "The key is a clear scope and a clear plan so expectations stay aligned.",
      "We focus on a site that launches fast and converts enquiries.",
    ],
    problemBoxHeading: "This works best if you",
    problemBullets: [
      "Need a new website urgently",
      "Have a clear business offer",
      "Can provide content quickly",
      "Want a simple, practical site",
      "Want to grow enquiries",
      "Want clear milestones and delivery",
    ],
    offerHeading: "What You Get",
    offerLead: "A focused website build with clear milestones and next steps.",
    offerCards: [
      { title: "Clear scope", text: "A simple page set designed for enquiries." },
      { title: "Fast build", text: "Launch-first approach with tight feedback loops." },
      { title: "Trust and proof", text: "Credibility blocks placed where they matter." },
      { title: "Mobile-first", text: "Built for real customers on phones." },
      { title: "Conversion CTAs", text: "Clear call and contact actions." },
      { title: "Application review", text: "We confirm fit and expectations first." },
    ],
    stepsHeading: "How It Works",
    steps: [
      { title: "Apply", text: "Short eligibility chat and scope confirmation." },
      { title: "Scope", text: "Lock pages, messaging, and milestones." },
      { title: "Build", text: "Design and build with fast feedback." },
      { title: "Launch", text: "Go live and confirm enquiry flow." },
    ],
    finalHeading: "Want To See If You Qualify?",
    finalLead: "Message us and we will explain the eligibility and milestones.",
  },

  {
    key: "build-now-pay-later",
    path: "/build-now-pay-later",
    title: "Build Now Pay Later Website | Clear Milestones | 2merative",
    description:
      "Build now pay later website option with clear milestones. A practical approach for small businesses that need momentum.",
    heroHeading: "Build Now. Pay Later. Clear Milestones.",
    heroSubheading:
      "A practical option for small businesses that need a website live and working. Clear scope and clear delivery.",
    problemHeading: "Waiting Costs You Leads",
    problemParagraphs: [
      "If customers cannot find you or trust you online, they choose someone else.",
      "A staged approach can help you launch sooner with clear steps.",
      "We keep the site focused on enquiries, not complexity.",
    ],
    problemBoxHeading: "This is ideal if you",
    problemBullets: [
      "Need to launch soon",
      "Want a staged plan",
      "Want clear deliverables",
      "Want a simple site that converts",
      "Can provide content quickly",
      "Want a clean, modern presence",
    ],
    offerHeading: "What You Get",
    offerLead: "A staged build designed to get you live and converting.",
    offerCards: [
      { title: "Stage 1 launch", text: "Core pages live quickly." },
      { title: "Stage 2 refinement", text: "Add proof and polish based on feedback." },
      { title: "Conversion CTAs", text: "Clear next steps for calls and messages." },
      { title: "Mobile-first UX", text: "Designed for phones first." },
      { title: "Basic tracking", text: "See what is working." },
      { title: "Clear scope", text: "No surprises or confusion." },
    ],
    stepsHeading: "How It Works",
    steps: [
      { title: "Quick chat", text: "Confirm scope and best staged approach." },
      { title: "Stage 1", text: "Launch the core site." },
      { title: "Stage 2", text: "Add refinements and improvements." },
      { title: "Ongoing", text: "Optional support after launch." },
    ],
    finalHeading: "Want A Website Live Fast?",
    finalLead: "Message us and we will explain the staged option clearly.",
  },

  {
    key: "fixed-price-website-packages",
    path: "/fixed-price-website-packages",
    title: "Fixed Price Website Packages | Clear Scope | 2merative",
    description:
      "Fixed price website packages with clear scope and deliverables. No confusion. Built to drive enquiries for small business.",
    heroHeading: "Fixed Price Website Packages With Clear Scope",
    heroSubheading:
      "You get clarity on what is included, what it costs, and what the next step is.",
    problemHeading: "People Want Certainty",
    problemParagraphs: [
      "Many businesses avoid web projects because they fear scope creep and unclear costs.",
      "A fixed scope package removes confusion.",
      "We keep packages practical and conversion-focused.",
    ],
    problemBoxHeading: "What we keep clear",
    problemBullets: [
      "Pages included",
      "What you provide",
      "What we build",
      "Timeframe",
      "What is optional",
      "What happens after launch",
    ],
    offerHeading: "What You Get",
    offerLead: "A clear website scope designed for enquiries.",
    offerCards: [
      { title: "Clear page set", text: "The pages you need, not endless extras." },
      { title: "Conversion structure", text: "Clear headings and CTAs throughout." },
      { title: "Trust blocks", text: "Proof where visitors decide." },
      { title: "Mobile-first build", text: "Fast and readable on phones." },
      { title: "Basic SEO structure", text: "Clean structure that supports local intent." },
      { title: "Launch support", text: "Go live with confidence." },
    ],
    stepsHeading: "How It Works",
    steps: [
      { title: "Choose scope", text: "Pick the package that fits." },
      { title: "Content", text: "We gather the essentials quickly." },
      { title: "Build", text: "Design and implement." },
      { title: "Launch", text: "Test and publish." },
    ],
    finalHeading: "Want A Clear Fixed Scope Website?",
    finalLead: "Message us and we will recommend the best package.",
  },

  {
    key: "website-health-check-first",
    path: "/website-health-check-first",
    title: "Website Health Check First | Decide With Confidence | 2merative",
    description:
      "Start with a website health check so you know what is broken and what to fix first. Clear priorities and next steps.",
    heroHeading: "Website Health Check First. Decide With Confidence.",
    heroSubheading:
      "If you are unsure whether to fix or rebuild, start with a simple health check and a ranked plan.",
    problemHeading: "Most Businesses Do Not Need Guesswork",
    problemParagraphs: [
      "A health check shows you what is costing enquiries right now.",
      "You get clear priorities, not a long technical report.",
      "Then you decide if you want us to implement the fixes.",
    ],
    problemBoxHeading: "What we check",
    problemBullets: [
      "Conversion path and CTAs",
      "Mobile usability",
      "Speed and performance",
      "Trust signals",
      "Basic visibility structure",
      "Contact form reliability",
    ],
    offerHeading: "What You Get",
    offerLead: "A clear shortlist of the highest-impact fixes.",
    offerCards: [
      { title: "Priority list", text: "Top items ranked by impact." },
      { title: "Quick wins", text: "Fast changes that improve leads." },
      { title: "Fix vs rebuild advice", text: "Choose the simplest path." },
      { title: "Plain language", text: "No jargon, clear actions." },
      { title: "Optional scope", text: "Clear options if you want us to fix it." },
      { title: "Next steps", text: "A plan you can act on immediately." },
    ],
    stepsHeading: "How It Works",
    steps: [
      { title: "Send link", text: "We review your site." },
      { title: "Health check", text: "We assess key issues." },
      { title: "Plan", text: "We deliver ranked actions." },
      { title: "Optional build", text: "We implement if you want." },
    ],
    finalHeading: "Want Clarity Before Spending More Money?",
    finalLead: "Send your site link and we will recommend the best next steps.",
  },

]